import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import toast from "react-hot-toast";
import { convertMessagesTimestamps, convertToLocalTime } from "../../../utils/timestampUtils";
import { getSocketId } from "../../../utils/socketHandler";
import { getAuthToken, refreshTokenFromCookie } from "../../../utils/getAuthToken";
import { getUsageLimitMessage } from "../../../utils/usageLimitMessages";
import { getCached, setCached, invalidate, STORES, TTL } from "../../../utils/indexedDBCache";
import { sanitizeOutboundChatDirection } from "../../../utils/socketSharedState";
import { isQrSendRefusal, qrRefusalToastId } from "../../../utils/qrSendRefusal";
import { isConsoleVerbose } from "../../../utils/consoleGate";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Debouncing for API calls to prevent spam
const debounceMap = new Map();
const DEBOUNCE_TIME = 100; // 100ms for messages (very short since they're time-sensitive)

// Simple cache for messages to speed up conversation loading
const messageCache = new Map();
const MESSAGE_CACHE_TIME = 30000; // 30 seconds cache

// Global status update cache - stores status updates by messageId
// This allows status updates to persist even when the contact isn't currently loaded
const statusUpdateCache = new Map(); // Map<messageId, { contactId, status, timestamp }>

// A send attempt made during an active QR burst-pause, or after the workspace's daily send cap
// is already exhausted, is now rejected synchronously (sendMessage/sendImage/sendVideo/sendAudio/
// sendDocument.service.js's baileys branches check both before ever creating a "queued"
// ChatMessage — see whatsAppService.js's checkBaileysBurstPause/checkBaileysDailyLimitReached).
// InboxAll.jsx already shows its own broadcast toast the MOMENT a pause begins (whatsapp_qr:
// burst_pause) and the same pattern covers the 80/90/100% daily-cap warnings (whatsapp_qr:
// daily_limit_alert) — but those broadcast toasts auto-dismiss, so an agent who tries to send
// later, after either condition is already in effect, would otherwise get no feedback beyond the
// message bubble silently failing to even appear. One shared toast id per condition so a
// broadcast and a per-attempt notice for the same condition never stack redundantly.
// (The ids live in utils/qrSendRefusal.js, shared with the global network error handler, which shows the same toast the
// moment the refusal arrives; the same id means the two never stack.)
function notifyIfBaileysBlocked(errBody) {
  if (isQrSendRefusal(errBody) && errBody.message) {
    toast.error(errBody.message, { id: qrRefusalToastId(errBody.code), duration: 6000 });
  }
}

// A QR send the server REFUSED on purpose (resting after a burst, daily cap reached ...) already told the user through the
// toast above, so it must not ALSO be stored as the chat's error (ChatBox shows that as a second message at the top of
// the chat). The thunk notes the refusal by the message's tempId; the rejected handler asks once and skips state.error.
// Every other failure (Meta, Instagram, Facebook, network ...) is untouched. The message itself is still marked failed.
const qrRefusedTempIds = new Set();
function rememberQrRefusal(errBody, tempId) {
  if (!tempId || !isQrSendRefusal(errBody)) return;
  if (qrRefusedTempIds.size > 200) qrRefusedTempIds.clear();
  qrRefusedTempIds.add(String(tempId));
}
function takeQrRefusal(tempId) {
  if (!tempId) return false;
  return qrRefusedTempIds.delete(String(tempId));
}

/** Server/socket may use senderid or senderId — normalize for lookups and merges */
const resolveChatSenderId = (chat) => {
  if (!chat) return undefined;
  const v = chat.senderid ?? chat.senderId;
  return v != null && v !== "" ? String(v) : undefined;
};

const resolveMergedSenderId = (incomingChat, existingMsg) => {
  const fromIn = resolveChatSenderId(incomingChat);
  const fromEx = existingMsg ? resolveChatSenderId(existingMsg) : undefined;
  if (fromIn) return fromIn;
  if (fromEx) return fromEx;
  return undefined;
};

/**
 * Socket partial payloads may set sendername to "Automated" while Redux already has the real team name + senderid.
 * Never downgrade team member names in that case (WhatsApp outbound team sends).
 */
const mergeSendernameAvoidAutomatedDowngrade = (existingMessage, incomingSendername) => {
  const inc =
    incomingSendername != null && incomingSendername !== ""
      ? String(incomingSendername)
      : "";
  const ex =
    existingMessage?.sendername != null && existingMessage?.sendername !== ""
      ? String(existingMessage.sendername)
      : "";
  const existingSid = existingMessage?.senderid ?? existingMessage?.senderId;
  // Never apply literal "Automated" onto a row that already has a team sender id (real agent send)
  if (inc === "Automated" && existingSid) {
    if (ex && ex !== "Automated") return ex;
    return ex;
  }
  if (inc) return inc;
  return ex;
};

/** Delivery rank for customer-originated rows (higher = closer to "read" in UI). */
const incomingDeliveryRank = (status) => {
  if (!status) return 0;
  const s = String(status).toLowerCase();
  if (s === "seen") return 4;
  if (s === "read") return 3;
  if (s === "delivered") return 2;
  if (s === "received") return 1;
  return 0;
};

/** Same outgoing detection as markMessageAsRead.fulfilled — do not alter outbound/workflow rows. */
const isOutgoingChatForReadPreserve = (chat, phoneNumberID) => {
  if (!chat) return true;
  const rawSid = chat.senderid ?? chat.senderId;
  const isBulkMsg = chat.isBulkMessage === true;
  const messageId = chat.id ? String(chat.id).trim() : null;
  const tempId = chat.tempId ? String(chat.tempId).trim() : null;
  const isWorkflowMsg =
    (messageId && messageId.startsWith("workflow_")) ||
    (tempId && tempId.startsWith("workflow_"));
  const hasNoSenderId = !rawSid;
  const fromMatchesBusiness =
    phoneNumberID &&
    chat.from &&
    (chat.from === phoneNumberID || chat.from.toString() === phoneNumberID.toString());
  const isWorkflowBulkByFrom = hasNoSenderId && fromMatchesBusiness;
  const isAutomatedMessage = !rawSid && !chat.clientid && !chat.clientname;
  const isWorkflowBulk =
    isBulkMsg || isWorkflowMsg || isWorkflowBulkByFrom || isAutomatedMessage;
  const isOutgoingBySenderId = !!rawSid;
  const isOutgoingByFrom =
    phoneNumberID &&
    chat.from &&
    (chat.from === phoneNumberID || chat.from.toString() === phoneNumberID.toString());
  return isOutgoingBySenderId || isOutgoingByFrom || isWorkflowBulk;
};

/**
 * If fetchMessagesByContact completes after markMessageAsRead, the API payload can still
 * contain received/delivered. Keep read/seen already applied in Redux for incoming chats.
 */
const chatKeysForReadPreserve = (c) => {
  const raw = [c?.id, c?._id, c?.tempId].filter((k) => k != null && k !== "");
  return [...new Set(raw.map((k) => String(k).trim()))];
};

/** ObjectId vs string — must match for read-preservation prior list */
const inboxContactIdsMatch = (a, b) =>
  a != null && b != null && String(a).trim() === String(b).trim();

/**
 * When fetchMessagesByContact fulfills after markMessageAsRead, API rows can still be
 * received/delivered. Prefer read/seen already in Redux; use all id aliases so rows match.
 */
const applyStaleFetchIncomingReadPreserve = (mergedChats, priorChats, phoneNumberID) => {
  if (!mergedChats?.length || !priorChats?.length) return mergedChats;
  const map = new Map();
  priorChats.forEach((c) => {
    for (const key of chatKeysForReadPreserve(c)) {
      if (!map.has(key)) map.set(key, c);
    }
  });
  return mergedChats.map((chat) => {
    if (isOutgoingChatForReadPreserve(chat, phoneNumberID)) return chat;
    let existing = null;
    for (const key of chatKeysForReadPreserve(chat)) {
      if (map.has(key)) {
        existing = map.get(key);
        break;
      }
    }
    if (!existing) return chat;
    const incR = incomingDeliveryRank(chat.status);
    const exR = incomingDeliveryRank(existing.status);
    if (exR > incR) {
      return { ...chat, status: existing.status };
    }
    return chat;
  });
};

// Our OWN outgoing messages only ever move FORWARD along this ladder (pending -> sent -> delivered -> read -> seen).
const OUTGOING_STATUS_LADDER = { pending: 0, sent: 1, delivered: 2, read: 3, seen: 4 };

/**
 * Which of two statuses of the same outgoing message is further along. `current` wins ties and anything it cannot rank (internal,
 * queued, sending, unknown, ... are never touched). "failed" replaces pending/sent only, and is itself replaced by delivered/read/seen --
 * the same relationships updateMessageStatus applies to live events.
 */
const furtherAlongOutgoingStatus = (current, other) => {
  const cur = current == null ? "" : String(current).toLowerCase().trim();
  const oth = other == null ? "" : String(other).toLowerCase().trim();
  if (!cur || !oth || cur === oth) return current;
  if (oth === "failed") return cur === "pending" || cur === "sent" ? other : current;
  if (cur === "failed") return oth === "delivered" || oth === "read" || oth === "seen" ? other : current;
  const rankCur = OUTGOING_STATUS_LADDER[cur];
  const rankOth = OUTGOING_STATUS_LADDER[oth];
  if (rankCur === undefined || rankOth === undefined) return current;
  return rankOth > rankCur ? other : current;
};

/**
 * Reopening a chat first paints a saved snapshot (taken when a message arrived, e.g. while it was still "pending") and then merges the
 * fresh server page into it. The merge reused the on-screen rows whenever the message ids had not changed, so a message that was read
 * in the meantime kept showing its old clock / single tick until a page refresh. For every OUTGOING row that exists both on screen
 * (`priorChats`) and in the fresh page (`apiChats`), keep whichever status is further along -- forward only, so a newer live status is
 * never downgraded by a server row that is momentarily behind, and a stale snapshot is brought up to date. Only `status` is touched;
 * incoming messages and anything outside the ladder are left alone. Returns the SAME array when nothing changes (no extra re-render).
 */
const applyFurtherOutgoingStatuses = (mergedChats, priorChats, apiChats, phoneNumberID) => {
  if (!Array.isArray(mergedChats) || mergedChats.length === 0) return mergedChats;
  if (!Array.isArray(priorChats) || priorChats.length === 0) return mergedChats;
  if (!Array.isArray(apiChats) || apiChats.length === 0) return mergedChats;
  const byKey = (list) => {
    const map = new Map();
    list.forEach((c) => {
      for (const key of chatKeysForReadPreserve(c)) {
        if (!map.has(key)) map.set(key, c);
      }
    });
    return map;
  };
  const maps = [byKey(priorChats), byKey(apiChats)];
  let changed = false;
  const result = mergedChats.map((chat) => {
    if (!isOutgoingChatForReadPreserve(chat, phoneNumberID)) return chat;
    let status = chat.status;
    for (const map of maps) {
      let counterpart = null;
      for (const key of chatKeysForReadPreserve(chat)) {
        if (map.has(key)) {
          counterpart = map.get(key);
          break;
        }
      }
      if (counterpart && counterpart !== chat) status = furtherAlongOutgoingStatus(status, counterpart.status);
    }
    if (status === chat.status) return chat;
    changed = true;
    return { ...chat, status };
  });
  return changed ? result : mergedChats;
};

// Helper function to find a cached status update for a message by checking all ID variations
const findCachedStatusUpdate = (message, normalizedContactId) => {
  if (!message || !normalizedContactId) return null;
  
  // Check all possible ID fields
  const messageIdStr = message.id ? String(message.id).trim() : null;
  const messageTempIdStr = message.tempId ? String(message.tempId).trim() : null;
  const message_IdStr = message._id ? String(message._id).trim() : null;
  
  // Try to find a cached status update for this message
  // Check all possible ID variations
  const cachedUpdate = (messageIdStr && statusUpdateCache.get(messageIdStr)) ||
                       (messageTempIdStr && statusUpdateCache.get(messageTempIdStr)) ||
                       (message_IdStr && statusUpdateCache.get(message_IdStr));
  
  if (cachedUpdate) {
    // Verify the cached update is for this contact
    const cachedContactId = cachedUpdate.contactId ? String(cachedUpdate.contactId).trim() : null;
    if (cachedContactId === normalizedContactId) {
      return cachedUpdate;
    }
  }
  
  return null;
};

// Helper function to apply cached status updates to messages
const applyCachedStatusUpdates = (chats, contactId, phoneNumberID = null) => {
  if (!chats || !Array.isArray(chats) || statusUpdateCache.size === 0) {
    return { chats, appliedCount: 0 };
  }
  
  const normalizedContactId = contactId ? String(contactId).trim() : null;
  if (!normalizedContactId) {
    return { chats, appliedCount: 0 };
  }
  
  const precedence = {
    seen: 4,
    read: 3,
    delivered: 2,
    sent: 1,
    pending: 0,
    failed: -1,
  };
  
  let appliedCount = 0;
  const updatedChats = chats.map((message) => {
    const cachedUpdate = findCachedStatusUpdate(message, normalizedContactId);
    
    if (cachedUpdate) {
      const currentStatus = message.status;
      const newStatus = cachedUpdate.status;
      const currentPrecedence = precedence[currentStatus] ?? -1;
      const newPrecedence = precedence[newStatus] ?? -1;
      
      // Check if this is a workflow/bulk message
      const isBulkMsg = message.isBulkMessage === true;
      const messageId = message.id ? String(message.id).trim() : null;
      const tempId = message.tempId ? String(message.tempId).trim() : null;
      const isWorkflowMsg = (messageId && messageId.startsWith('workflow_')) || 
                           (tempId && tempId.startsWith('workflow_'));
      const hasNoSenderId = !message.senderid;
      const fromMatchesBusiness = phoneNumberID && message.from && 
                                 (message.from === phoneNumberID || message.from.toString() === phoneNumberID.toString());
      const isWorkflowBulkByFrom = hasNoSenderId && fromMatchesBusiness;
      const isAutomatedMessage = !message.senderid && !message.clientid && !message.clientname;
      const isWorkflowBulk = isBulkMsg || isWorkflowMsg || isWorkflowBulkByFrom || isAutomatedMessage;
      
      // Check if this is a normal outgoing message (has senderid)
      const isOutgoingMessage = !!message.senderid && !message.clientid && !message.clientname;
      
      // CRITICAL: For workflow/bulk messages, only apply "read" status if cached update confirms it
      // Don't apply other statuses that might override our correction
      if (isWorkflowBulk) {
        // Only apply "read" or "seen" from cache (real-time webhook confirmation)
        // Don't apply other statuses that might downgrade incorrectly
        // One addition: a row that is still "pending" is a WhatsApp template (workflow / partner API /
        // inbox) waiting for Meta's confirmation — its sent / delivered / failed update may have
        // arrived before the row was loaded, and without it the row would sit on the clock icon.
        // A pending row can only move forward (never down from read/delivered), so this is safe.
        const resolvesPending = currentStatus === "pending" && (newStatus === "sent" || newStatus === "delivered" || newStatus === "failed");
        if (newStatus === "read" || newStatus === "seen" || resolvesPending) {
          appliedCount++;
          console.log(`✅ [CACHED STATUS UPDATE] Applied "read" status for workflow/bulk message (webhook confirmed):`, {
            messageId: message.id || message._id || message.tempId,
            oldStatus: currentStatus,
            newStatus: newStatus
          });
          // NOTE: status only — never touch timestamp/createdAt here. Those are the
          // real-time sort keys (see addMessage's resort); overwriting timestamp with
          // a status-change time desyncs it from send order and reshuffles the list
          // the next time any message triggers a resort.
          return {
            ...message,
            status: newStatus,
          };
        } else {
          // Don't apply non-read statuses to workflow/bulk messages
          // Our correction logic already handled this
          return message;
        }
      }
      
      // For normal outgoing messages, apply webhook updates as before
      // CRITICAL: For outgoing messages, webhook status updates are authoritative
      // BUT: We should NOT allow "sent" or "pending" to overwrite "read" or "seen" status
      // Only allow "delivered" to overwrite "read" if it's from a webhook (recent timestamp)
      // This prevents client-side "sent" status updates from downgrading correct "read" statuses
      const isCorrectingIncorrectReadStatus = isOutgoingMessage && 
        (currentStatus === "read" || currentStatus === "seen") &&
        (newStatus === "delivered"); // Only allow "delivered" to correct "read", not "sent" or "pending"
      
      // Apply update if:
      // 1. New status has higher precedence (normal case)
      // 2. Statuses are the same (no change needed)
      // 3. For outgoing messages, allow webhook updates to correct incorrect "read" status (only "delivered")
      // "failed" may replace "pending" only (same rule as the live updateMessageStatus reducer).
      const isFailedReplacingPending = newStatus === "failed" && currentStatus === "pending";
      const shouldApply = newPrecedence > currentPrecedence || 
                         newStatus === currentStatus || 
                         isCorrectingIncorrectReadStatus ||
                         isFailedReplacingPending;
      
      if (shouldApply) {
        appliedCount++;
        console.log(`✅ [CACHED STATUS UPDATE] Applied cached status update:`, {
          messageId: message.id || message._id || message.tempId,
          oldStatus: currentStatus,
          newStatus: newStatus,
          isOutgoingMessage,
          corrected: isCorrectingIncorrectReadStatus
        });
        // NOTE: status only — see comment above; timestamp must stay untouched.
        return {
          ...message,
          status: newStatus,
        };
      }
    }
    return message;
  });
  
  if (appliedCount > 0) {
    console.log(`✅ [CACHED STATUS UPDATE] Applied ${appliedCount} cached status updates for contact ${normalizedContactId}`);
  }
  
  return { chats: updatedChats, appliedCount };
};

// Track recent message sends to prevent duplicates
const recentSends = new Map();
const SEND_DEBOUNCE_TIME = 2000; // 2 seconds - prevent duplicate sends

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);
  
  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }
  
  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears every module-level cache/debounce map — call on logout so the next
// account's fetches/sends aren't skipped or served from the previous account's data.
export const __resetInboxMessageCaches = () => {
  debounceMap.clear();
  messageCache.clear();
  statusUpdateCache.clear();
  recentSends.clear();
};

// In-memory cache is checked first (fastest). On miss, fall back to IndexedDB
// which survives page refresh and has a longer TTL (30 min vs 30 sec).
const getCachedMessages = async (contactId) => {
  const inMemory = messageCache.get(contactId);
  if (inMemory && (Date.now() - inMemory.timestamp) < MESSAGE_CACHE_TIME) {
    return inMemory.data;
  }
  const fromIDB = await getCached(STORES.MESSAGES, contactId);
  if (fromIDB) {
    // Warm up in-memory so subsequent hits within 30 s are instant.
    messageCache.set(contactId, { data: fromIDB, timestamp: Date.now() });
    return fromIDB;
  }
  return null;
};

const setCachedMessages = (contactId, data) => {
  messageCache.set(contactId, { data, timestamp: Date.now() });
  // IDB write is fire-and-forget — never block the hot path.
  setCached(STORES.MESSAGES, contactId, data, TTL.MESSAGES);
};

// Helper function to convert timestamps to ISO strings for Redux serialization
const toISOString = (timestamp) => {
  if (!timestamp) return new Date().toISOString();
  if (typeof timestamp === 'string' && timestamp.includes('T')) {
    // Already an ISO string
    return timestamp;
  }
  if (timestamp instanceof Date) {
    return timestamp.toISOString();
  }
  if (typeof timestamp === 'number') {
    return new Date(timestamp).toISOString();
  }
  try {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
  } catch {
    return new Date().toISOString();
  }
};

// Like toISOString above, but preserves "missing" as null instead of defaulting to
// now() — used when normalizing a single raw timestamp field off an API chat object,
// where falling back to the current time would silently fabricate a wrong value.
const toRawTimestampISOOrNull = (value) => {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date.toISOString();
  }
  return null;
};

// Async thunk for fetching messages by contact ID with pagination
// Background refresh thunk — fires when IDB serves stale cached data.
// Fetches fresh API data, merges in any socket messages already in Redux,
// updates IDB, and returns the merged payload so the reducer can update state.
const refreshMessagesBackground = createAsyncThunk(
  "inboxMessages/refreshMessagesBackground",
  async (contactId, { dispatch }) => {
    // Dispatch fetchMessagesByContact with skipCache=true so the fresh API data
    // goes through the full normalization pipeline and updates Redux state correctly.
    // The fulfilled reducer for fetchMessagesByContact handles everything —
    // no need for a separate raw-data handler here.
    dispatch(fetchMessagesByContact({ contactId, skipCache: true }));
  }
);

export const fetchMessagesByContact = createAsyncThunk(
  "inboxMessages/fetchMessagesByContact",
  async ({ contactId, limit = 50, offset = 0, messageId = null, direction = 'latest', skipCache = false }, { rejectWithValue, getState, dispatch }) => {
    // Don't use cache for paginated requests (only for initial load)
    const isInitialLoad = offset === 0 && !messageId && direction === 'latest';

    if (isInitialLoad && !skipCache) {
      const cachedMessages = await getCachedMessages(contactId);
      if (cachedMessages) {
        const cachedData = cachedMessages.data;
        // Reject cache entries from before pagination was implemented.
        // Old entries lack hasMore/totalChats metadata and may contain the full
        // message history, which causes UI freezes when loaded into Redux.
        // A valid paginated cache entry always has hasMore + totalChats metadata.
        // Do NOT add a chats.length <= limit check: the cache may legitimately hold
        // slightly more than `limit` messages when in-flight socket arrivals were
        // merged into the response before the IDB write.
        const isPaginatedCache =
          cachedData?.hasMore !== undefined &&
          cachedData?.totalChats !== undefined &&
          Array.isArray(cachedData?.chats);
        if (isPaginatedCache) {
          // Serve stale IDB data immediately, then fire a background refresh
          // so the user gets fresh messages without a loading spinner.
          dispatch(refreshMessagesBackground(contactId));
          return cachedMessages;
        }
        // Old format: evict and fall through to a fresh API call.
        messageCache.delete(contactId);
        invalidate(STORES.MESSAGES, contactId);
      }
    }

    // Debounce check per contact (only for initial loads, not for background refreshes)
    if (isInitialLoad && !skipCache && shouldDebounce(`fetchMessagesByContact_${contactId}`)) {
      
      // Check if we have existing messages for this contact to avoid loading state
      const state = getState();
      if (state.inboxMessage.messages && state.inboxMessage.messages.contactID === contactId) {
        // Return existing messages to avoid loading state
        return {
          data: state.inboxMessage.messages,
          contactId: contactId
        };
      }
      
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());
      if (messageId) params.append('messageId', messageId);
      if (direction) params.append('direction', direction);
      
      const queryString = params.toString();
      const url = `${SERVER_DOMAIN}/api/messages/getbycontact/${contactId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const result = { 
        data: response.data, 
        contactId,
        isInitialLoad,
        isPagination: !isInitialLoad
      };
      
      // Only cache initial loads
      if (isInitialLoad) {
        // Merge in any socket messages that arrived while this API call was in-flight
        // so IDB never stores a snapshot that's missing real-time messages.
        const currentState = getState();
        const existingMessages = currentState.inboxMessage.messages;
        let dataToCache = response.data;

        if (
          existingMessages &&
          String(existingMessages.contactID || existingMessages.contactId || "") === String(contactId) &&
          Array.isArray(existingMessages.chats) &&
          Array.isArray(response.data.chats)
        ) {
          const apiIds = new Set(response.data.chats.map((c) => c.id || c._id || c.tempId).filter(Boolean));
          // Own sends the server has confirmed and already lists under their real id are NOT socket-only: appending the saved temp-
          // bubble here put the same message into this response AND into the cached snapshot twice (see findSupersededOptimisticChatIds).
          const supersededOwnSends = findSupersededOptimisticChatIds(existingMessages.chats, response.data.chats);
          const socketOnlyChats = existingMessages.chats.filter((c) => {
            const id = c.id || c._id || c.tempId;
            return id && !apiIds.has(id) && !supersededOwnSends.has(String(id));
          });
          if (socketOnlyChats.length > 0) {
            dataToCache = { ...response.data, chats: [...response.data.chats, ...socketOnlyChats] };
          }
        }

        const result = { data: dataToCache, contactId, isInitialLoad, isPagination: !isInitialLoad };
        setCachedMessages(contactId, result);
        return result;
      }

      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch messages for the contact"
      );
    }
  }
);

// Group analogue of fetchMessagesByContact (WhatsApp Groups plan, Phase G7) -- deliberately
// much simpler, not a fork of it: no IndexedDB caching/background-refresh/status-correction
// merge, since GroupChatMessageModel rows are always freshly written by this feature's own
// inbound/outbound pipeline (baileysGroupInboundHandler.js / baileysSendWorker.js), unlike the
// 1:1 path's status-correction logic which exists to paper over years of legacy data. Writes
// into the SAME state.messages/state.loading/state.expectedContactId fields fetchMessagesByContact
// uses -- only one thread (contact or group) is ever open at a time, and the backend response
// shape (getMessagesByGroup.service.js) matches getMessagesByContact's chatData contract field
// for field, so ChatBody.jsx's rendering needs no changes to consume either.
export const fetchMessagesByGroup = createAsyncThunk(
  "inboxMessages/fetchMessagesByGroup",
  async ({ groupId, limit = 50, offset = 0 }, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());
      const url = `${SERVER_DOMAIN}/api/messages/getbygroup/${groupId}?${params.toString()}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      return { data: response.data, groupId, isInitialLoad: offset === 0, isPagination: offset > 0 };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch messages for the group"
      );
    }
  }
);

// Group analogue of fetchOlderMessages -- same offset-increment pagination shape.
export const fetchOlderGroupMessages = createAsyncThunk(
  "inboxMessages/fetchOlderGroupMessages",
  async ({ groupId, limit = 50 }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const currentMessages = state.inboxMessage.messages;
    const currentOffset = currentMessages?.offset || 0;
    const totalChats = currentMessages?.totalChats || 0;

    if (currentMessages?.hasMore === false || currentOffset + limit >= totalChats) {
      return rejectWithValue("No older messages available");
    }

    const newOffset = currentOffset + limit;
    const result = await dispatch(fetchMessagesByGroup({ groupId, limit, offset: newOffset }));
    return result.payload || result;
  }
);

// Async thunk for loading older messages (pagination)
export const fetchOlderMessages = createAsyncThunk(
  "inboxMessages/fetchOlderMessages",
  async ({ contactId, limit = 50 }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const currentMessages = state.inboxMessage.messages;
    
    // Get current offset from state
    // Offset represents how many messages from the end we've already loaded
    // offset=0 means we're showing the last 50 messages
    // To load older, we increase offset by limit
    const currentOffset = currentMessages?.offset || 0;
    const totalChats = currentMessages?.totalChats || 0;
    
    // Check if there are more messages to load
    if (currentMessages?.hasMore === false || currentOffset + limit >= totalChats) {
      return rejectWithValue("No older messages available");
    }
    
    // Calculate new offset (increase by limit to go further back)
    const newOffset = currentOffset + limit;
    
    // Dispatch the fetchMessagesByContact thunk with direction='older'
    const result = await dispatch(fetchMessagesByContact({ 
      contactId, 
      limit, 
      offset: newOffset, 
      direction: 'older' 
    }));
    
    return result.payload || result;
  }
);

// Async thunk for fetching messages around a specific message (for reply navigation)
export const fetchMessagesAroundMessage = createAsyncThunk(
  "inboxMessages/fetchMessagesAroundMessage",
  async ({ contactId, messageId, limit = 50 }, { rejectWithValue, dispatch }) => {
    const mid =
      messageId != null && String(messageId).trim() !== ""
        ? String(messageId).trim()
        : null;
    console.log("[REPLY-JUMP] fetchMessagesAroundMessage dispatch", {
      contactId,
      messageIdPrefix: mid ? mid.slice(0, 48) : null,
      limit,
    });
    // Dispatch the fetchMessagesByContact thunk
    const result = await dispatch(fetchMessagesByContact({ 
      contactId, 
      limit, 
      offset: 0, 
      messageId: mid,
      direction: 'latest' 
    }));
    
    return result.payload || result;
  }
);

// Async thunk for sending a message
export const sendMessage = createAsyncThunk(
  "inboxMessages/sendMessage",
  async (
    {
      number,
      customMessage,
      AccountID,
      tempId,
      originSocketId,
      contactId,
      groupId,
      via,
      isInternalMention,
      mentionedTeamMemberId,
      mentions,
    },
    { rejectWithValue, dispatch, getState }
  ) => {
    // Prevent duplicate sends by checking recent sends (number or contactId for Instagram, groupId for a group)
    const recipientKey = number || contactId || groupId;
    const sendKey = `${recipientKey}_${customMessage}_${AccountID}`;
    const now = Date.now();
    const recentSend = recentSends.get(sendKey);
    
    if (recentSend && (now - recentSend) < SEND_DEBOUNCE_TIME) {
      return rejectWithValue("Duplicate send prevented");
    }
    
    // Track this send attempt
    recentSends.set(sendKey, now);
    
    // Clean up old entries to prevent memory leaks
    if (recentSends.size > 100) {
      const cutoff = now - (SEND_DEBOUNCE_TIME * 2);
      for (const [key, timestamp] of recentSends.entries()) {
        if (timestamp < cutoff) {
          recentSends.delete(key);
        }
      }
    }
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    
    const user = localStorage.getItem("user");
    const parsedUser = user ? JSON.parse(user) : null;
    const workspaceId = parsedUser ? parsedUser.workspaceId : null;
    // Computed once here so the fulfilled reducer doesn't need its own localStorage read/parse.
    const currentUser = parsedUser ? { _id: parsedUser._id, name: parsedUser.name } : null;

    if (!token) {
      console.error('❌ [CLIENT] No token found in localStorage');
      return rejectWithValue("No token found");
    }

    // Add retry logic with exponential backoff
    const maxRetries = 3;
    let retryCount = 0;
    let lastError = null;
    // QR chats (and every group, which only exists on QR) only: a request that ends WITHOUT an answer (timeout, dropped
    // connection, a gateway 502/504) does not mean the message was not sent -- the server may have saved and queued it, and then
    // it is delivered while the bubble sits red. Such an outcome is verified by asking the server again with the SAME tempId
    // (it answers "already sent" with the real status when it has the message, and sends it now when it never got it) before
    // anything is shown as failed. Meta / Instagram / Facebook keep the old behaviour exactly.
    const isQrSend = Boolean(groupId) || getState()?.inboxMessage?.messages?.platform === "qr";
    const MAX_VERIFY_ATTEMPTS = 2;
    let verifyAttempts = 0;

    while (retryCount < maxRetries) {
      try {
        // Add a small delay between retries to prevent overwhelming the server
        if (retryCount > 0) {
          const delay = Math.pow(2, retryCount) * 5000; // Exponential backoff: 2s, 4s, 8s
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        const requestPayload = {
          number,
          customMessage,
          AccountID,
          tempId,
          originSocketId,
          workspaceId,
          ...(contactId && { contactId }),
          ...(groupId && { groupId }),
          // "qr": reply through the linked QR number. "whatsapp_web": reply into the contact's browser chat (nothing goes to Meta).
          ...((via === "qr" || via === "whatsapp_web") && { via }),
          ...(typeof isInternalMention === "boolean" && { isInternalMention }),
          ...(mentionedTeamMemberId && { mentionedTeamMemberId }),
          ...(Array.isArray(mentions) && mentions.length > 0 && { mentions }),
        };

        const response = await axios.post(
          `${SERVER_DOMAIN}/api/messages/send-custom`,
          requestPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            timeout: 30000, // 30 second timeout
          }
        );

        if (!response.data.success) {
          throw new Error(response.data.message || "Failed to send message");
        }
        return { ...response.data, currentUser };
    } catch (error) {
        lastError = error;
        retryCount++;
        
        // Detailed error logging
        console.error(`❌ [CLIENT] Message send attempt ${retryCount} failed for tempId: ${tempId}`, {
          errorMessage: error.message,
          errorCode: error.code,
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
          hasResponse: !!error.response,
          isNetworkError: !error.response,
          stack: error.stack
        });
        
        // Don't retry on certain errors (client errors, server errors, or network issues)
        const status = error.response?.status;
        const isNetworkError = !error.response && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.message.includes('timeout'));

        // QR only: an unanswered request (see isQrSend above) is verified by re-asking with the same tempId. A real answer from
        // our API (a refusal with a JSON body: limits, burst pause, blocked ...) is never "uncertain" and fails at once, as before.
        const answeredByOurApi = error.response && error.response.data && typeof error.response.data === "object";
        const uncertainOutcome = !error.response || status === 502 || status === 504 || (status === 503 && !answeredByOurApi);
        if (isQrSend && uncertainOutcome && verifyAttempts < MAX_VERIFY_ATTEMPTS) {
          verifyAttempts++;
          retryCount--; // verifying does not use up the normal retry budget
          const verifyDelayMs = 3000 * verifyAttempts;
          await new Promise((resolve) => setTimeout(resolve, verifyDelayMs));
          continue;
        }
        
        if (status === 400 || status === 403 || status === 404 || status === 500 || status === 502 || status === 503 || isNetworkError) {
          break;
        }
        
        // If it's the last retry, break
        if (retryCount >= maxRetries) {
          break;
        }
      }
    }

    // If we get here, all retries failed
    const errBody = lastError.response?.data;
    notifyIfBaileysBlocked(errBody);
    rememberQrRefusal(errBody, tempId);
    if (errBody?.code === "LIMIT_EXCEEDED" || errBody?.code === "FEATURE_NOT_IN_PLAN") {
      return rejectWithValue(getUsageLimitMessage(errBody));
    }
    if (errBody?.message) {
      return rejectWithValue(getUsageLimitMessage(errBody));
    }
    return rejectWithValue(lastError.message || "Failed to send message after multiple attempts");
  }
);
// Async thunk for sending a gallery item directly (no file re-upload from browser)
export const sendGalleryItemMessage = createAsyncThunk(
  "inboxMessages/sendGalleryItemMessage",
  async ({ galleryItemId, AccountID, to, tempId, contactId, groupId, caption }, { rejectWithValue, getState }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/gallery/${galleryItemId}/send`,
        { AccountID, to, tempId, contactId, groupId, caption: caption || null, originSocketId: getSocketId() },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      const errBody = error.response?.data;
      if (errBody?.message) return rejectWithValue(errBody.message);
      return rejectWithValue(error.message || 'Failed to send gallery item');
    }
  }
);

// Async thunk for sending a catalog message (single/multi-product) picked
// from the inbox composer's Catalog button -- see InboxCatalogSend.jsx.
// Thin POST like sendGalleryItemMessage above (no file upload, no retry
// loop): server-side /send-catalog already handles the WhatsApp API call,
// chat-row creation and socket emission.
export const sendCatalogProductsMessage = createAsyncThunk(
  "inboxMessages/sendCatalogProductsMessage",
  async ({ number, catalogType, productData, AccountID, tempId, originSocketId, userId }, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/messages/send-catalog`,
        { number, catalogType, productData, AccountID, tempId, originSocketId, userId },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to send catalog message");
      }
      return response.data;
    } catch (error) {
      const errBody = error.response?.data;
      if (errBody?.message) return rejectWithValue(errBody.message);
      return rejectWithValue(error.message || "Failed to send catalog message");
    }
  }
);

// Shared upload implementation for sendImageByUpload/sendVideoByUpload/
// sendAudioByUpload/sendDocumentByUpload below -- these previously each
// duplicated the same FormData-build + retry + timeout-scaling + error-
// extraction logic with only the endpoint/field name/extra fields differing
// (and, before this, only sendImageByUpload actually had retry + a
// size-scaled timeout). Standardized here so all four media types get the
// same resilience against slow connections and large files.
async function performMediaUpload(
  { endpoint, fieldName, file, requiredFields = {}, optionalFields = {}, defaultErrorMessage },
  { rejectWithValue, getState }
) {
  const token = await getAuthToken();
  if (!token) return rejectWithValue("No token found");

  const formData = new FormData();
  formData.append(fieldName, file);
  for (const [key, value] of Object.entries(requiredFields)) {
    formData.append(key, value);
  }
  for (const [key, value] of Object.entries(optionalFields)) {
    if (value !== undefined && value !== null && value !== "") formData.append(key, value);
  }

  // Add the socket ID to track the original sender
  try {
    formData.append("originSocketId", getSocketId());
  } catch (error) {
    console.warn("Socket not initialized, messages might not sync between sessions");
  }

  // Scale timeout with file size: 60s base + 30s per MB (handles slow connections)
  const fileSizeMB = file?.size ? file.size / (1024 * 1024) : 2;
  const timeoutMs = Math.max(60000, Math.ceil(fileSizeMB) * 30000 + 30000);

  const maxRetries = 3;
  let lastError = null;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, Math.pow(2, attempt) * 2000));
      }
      const response = await axios.post(`${SERVER_DOMAIN}/api/messages/${endpoint}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: timeoutMs,
      });
      return response.data;
    } catch (error) {
      lastError = error;
      const status = error.response?.status;
      // Don't retry on definitive client/auth errors, or a QR burst-pause/daily-cap rejection --
      // that 503 is a definite, immediate "no" (see whatsAppService.js's synchronous checks),
      // not a transient failure retrying could fix; retrying it just delays the toast below by
      // several seconds for no benefit. sendMessage's own equivalent retry loop already excludes
      // 503 for this same reason.
      //
      // QR chats only: a 503 that is NOT a JSON answer from our API (the gateway answering while the server restarts) is not a
      // refusal -- the upload may have been saved -- so it is asked again with the same tempId (the server dedupes it) like any
      // other unanswered request, instead of ending the send as failed. Meta / Instagram / Facebook keep the rule exactly.
      const isQrUpload = Boolean(optionalFields?.groupId) || getState()?.inboxMessage?.messages?.platform === "qr";
      const answeredByOurApi = error.response && error.response.data && typeof error.response.data === "object";
      const gatewayOnly503 = status === 503 && isQrUpload && !answeredByOurApi;
      if (status === 400 || status === 401 || status === 403 || status === 404 || (status === 503 && !gatewayOnly503)) break;
    }
  }
  notifyIfBaileysBlocked(lastError?.response?.data);
  rememberQrRefusal(lastError?.response?.data, requiredFields?.tempId);
  const errorMessage =
    lastError?.response?.data?.message ||
    lastError?.response?.data?.error ||
    lastError?.message ||
    defaultErrorMessage;
  return rejectWithValue(errorMessage);
}

// Async thunk for sending an image
export const sendImageByUpload = createAsyncThunk(
  "inboxMessages/sendImageByUpload",
  async ({ image, AccountID, to, tempId, contactId, groupId, via }, thunkAPI) =>
    performMediaUpload(
      {
        endpoint: "sendImageByUpload",
        fieldName: "image",
        file: image,
        requiredFields: { AccountID, to, tempId },
        optionalFields: { contactId, groupId, via },
        defaultErrorMessage: "Failed to send the image",
      },
      thunkAPI
    )
);

// Async thunk for sending a video
export const sendVideoByUpload = createAsyncThunk(
  "inboxMessages/sendVideoByUpload",
  async ({ video, AccountID, to, tempId, caption, contactId, groupId, via }, thunkAPI) =>
    performMediaUpload(
      {
        endpoint: "sendVideoByUpload",
        fieldName: "video",
        file: video,
        requiredFields: { AccountID, to, tempId },
        optionalFields: { caption, contactId, groupId, via },
        defaultErrorMessage: "Failed to send the video",
      },
      thunkAPI
    )
);

// Async thunk for sending a Audio
export const sendAudioByUpload = createAsyncThunk(
  "inboxMessages/sendAudioByUpload",
  async ({ audio, AccountID, to, tempId, contactId, groupId, via, audioDurationSeconds }, thunkAPI) => {
    const validatedDurationSeconds =
      audioDurationSeconds != null &&
      audioDurationSeconds !== "" &&
      Number.isFinite(Number(audioDurationSeconds)) &&
      Number(audioDurationSeconds) > 0
        ? String(Math.round(Number(audioDurationSeconds)))
        : undefined;

    return performMediaUpload(
      {
        endpoint: "sendAudioByUpload",
        fieldName: "audio",
        file: audio,
        requiredFields: { AccountID, to, tempId },
        optionalFields: { contactId, groupId, via, audioDurationSeconds: validatedDurationSeconds },
        defaultErrorMessage: "Failed to send the audio",
      },
      thunkAPI
    );
  }
);

// Async thunk for sending a document (WhatsApp: to = phone number; Instagram: contactId)
export const sendDocumentByUpload = createAsyncThunk(
  "inboxMessages/sendDocumentByUpload",
  async ({ document, AccountID, to, contactId, groupId, via, workspaceId, tempId }, thunkAPI) =>
    performMediaUpload(
      {
        endpoint: "sendDocumentByUpload",
        fieldName: "document",
        file: document,
        requiredFields: { AccountID, tempId },
        optionalFields: { to, contactId, groupId, via, workspaceId },
        defaultErrorMessage: "Failed to send the document",
      },
      thunkAPI
    )
);

// Async thunk for marking messages as read
export const markMessageAsRead = createAsyncThunk(
  "inboxMessages/markMessageAsRead",
  async ({ AccountID, to, contactId, platform, autoMarkAsRead = false }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    let token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      // Get current socket ID for multi-browser session support
      let originSocketId = null;
      try {
        originSocketId = getSocketId();
      } catch (error) {
        console.warn(
          "Socket not initialized, messages might not sync between sessions"
        );
      }

      const response = await axios.post(
        `${SERVER_DOMAIN}/api/messages/markMessageAsRead`,
        { AccountID, to, contactId, platform, originSocketId, autoMarkAsRead },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000, // 10 second timeout
        }
      );
      
      return response.data;
    } catch (error) {
      console.error("📡 API call failed:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      // If 401, try to refresh token and retry
      if (error.response?.status === 401) {
        try {
          // Delegate to the single shared refresh implementation (getAuthToken.js)
          // so this joins any in-flight refresh instead of racing the single-use
          // refresh-token cookie against other callers (proactive interval, axios
          // interceptor, etc.)
          const newToken = await refreshTokenFromCookie();
          if (!newToken) {
            throw new Error('Token refresh failed');
          }

          // Get socket ID again for retry
          let originSocketId = null;
          try {
            originSocketId = getSocketId();
          } catch (socketError) {
            // Socket not initialized
          }

          // Retry the request with new token
          const retryResponse = await axios.post(
            `${SERVER_DOMAIN}/api/messages/markMessageAsRead`,
            { AccountID, to, contactId, platform, originSocketId, autoMarkAsRead },
            {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
              timeout: 10000,
            }
          );

          return retryResponse.data;
        } catch (refreshError) {
          console.error("❌ Token refresh failed:", refreshError);
          return rejectWithValue("Session expired - please login again");
        }
      }

      return rejectWithValue(
        error.response?.data || "Failed to mark messages as read"
      );
    }
  }
);

// Slice definition
const inboxMessageSlice = createSlice({
  name: "inboxMessages",
  initialState: {
    messages: null, // will store the object returned from API { contactID, totalChats, chats, ... }
    loading: false,
    messageSendingLoading: false,
    error: null,
    expectedContactId: null, // Track which contact we're expecting data for
  },
  reducers: {
    clearMessages(state) {
      console.warn("[DL-TRACE] clearMessages", new Error().stack);
      state.messages = null;
      state.loading = false;
      state.error = null;
      state.expectedContactId = null; // Clear expected contact ID
      
      // Also clear the message cache to ensure fresh data on next load
      messageCache.clear();
    },
    clearErrors(state) {
      state.error = null;
    },
    /** User-facing error for ChatBox Snackbar (e.g. failed to open chat); does not touch messages. */
    setInboxMessageUserError(state, action) {
      state.error = action.payload == null ? null : String(action.payload);
    },
    setExpectedContact(state, action) {
      state.expectedContactId = action.payload;
    },
    removeOptimisticMessage(state, action) {
      const { tempId } = action.payload || {};
      if (!state.messages?.chats || !tempId) return;
      state.messages.chats = state.messages.chats.filter(
        (msg) => msg.tempId !== tempId && msg.id !== tempId
      );
    },
    /**
     * Direct, unconditional find-by-id-and-merge — deliberately not routed
     * through addMessage, whose merge logic is conditioned on specific
     * historical cases (pending->server-data, internal-mention sendername)
     * and silently no-ops for anything that isn't one of those. This is the
     * primitive that should have existed for "a message's fields changed
     * after the fact" generally; calling's ringing->completed transition is
     * the first real user of it.
     */
    patchChatMessage(state, action) {
      const { messageId, patch } = action.payload || {};
      if (!state.messages?.chats || !messageId || !patch) return;
      const target = state.messages.chats.find((msg) => msg.id === messageId || msg._id === messageId);
      if (target) Object.assign(target, patch);
    },
    addMessage(state, action) {
      const newMessage = action.payload;

      if (newMessage.chat) {
        sanitizeOutboundChatDirection(newMessage.chat);
      }
      
      // CRITICAL: Get current user info early so it's available in all code paths
      const user = localStorage.getItem("user");
      const parsedUser = user ? JSON.parse(user) : null;
      const currentUserId = parsedUser?._id;
      
      // CRITICAL: Log all messages being added for debugging
      const isInternalMentionCheck = newMessage.chat?.isInternalMention || newMessage.chat?.status === "internal";
      if (isInternalMentionCheck) {
        console.log(`📥 [REDUX ADD MESSAGE] Received internal mention message:`, {
          messageId: newMessage.chat?.id || newMessage.chat?.tempId,
          sendername: newMessage.chat?.sendername,
          senderid: newMessage.chat?.senderid,
          status: newMessage.chat?.status,
          isInternalMention: newMessage.chat?.isInternalMention,
          isForActiveChat: newMessage.isForActiveChat,
          hasChat: !!newMessage.chat,
          senderAvatarColor: newMessage.chat?.senderAvatarColor,
          contactID: newMessage.contactID
        });
      }
      
      // Only add message if it's for the active chat
      if (!newMessage.isForActiveChat) {
        if (isInternalMentionCheck) {
          console.log(`⏭️ [REDUX ADD MESSAGE] Skipping internal mention (not for active chat):`, {
            messageId: newMessage.chat?.id || newMessage.chat?.tempId,
            isForActiveChat: newMessage.isForActiveChat
          });
        }
        return;
      }

      // CRITICAL: Prevent duplicate messages - check if message with same ID already exists
      // BUT: For pending messages, UPDATE them with server data instead of skipping
      // This prevents workflow messages with multiple keywords from being displayed twice
      const messageId = newMessage.chat?.id || newMessage.chat?._id || newMessage.chat?.tempId;
      const incomingTempId = newMessage.chat?.tempId;
      if (messageId && state.messages?.chats) {
        const existingMessageIndex = state.messages.chats.findIndex(
          chat =>
            chat.id === messageId ||
            chat._id === messageId ||
            chat.tempId === messageId ||
            // Match pending message by its tempId against the incoming tempId (handles the case
            // where the server second-emission arrives with a real wamid as id but the same tempId)
            (incomingTempId &&
              messageId !== incomingTempId &&
              (chat.id === incomingTempId || chat.tempId === incomingTempId))
        );
        
        if (existingMessageIndex !== -1) {
          const existingMessage = state.messages.chats[existingMessageIndex];
          
          // CRITICAL: Log when duplicate is found for debugging
          const isInternalMentionCheck = newMessage.chat?.isInternalMention || newMessage.chat?.status === "internal";
          if (isInternalMentionCheck) {
            const existingIsInternalCheck = existingMessage.status === "internal" || existingMessage.isInternalMention === true;
            const case1Match = existingMessage.status === "pending" && 
                               (newMessage.chat?.status === "internal" || 
                                newMessage.chat?.isInternalMention || 
                                newMessage.chat?.sendername);
            const case2Match = !!(existingIsInternalCheck && 
                               (!existingMessage.sendername || existingMessage.sendername === "" || existingMessage.sendername === "Automated") && 
                               newMessage.chat?.sendername);
            
            console.log(`🔍 [REDUX ADD MESSAGE] Duplicate found for internal mention:`, {
              messageId,
              existingStatus: existingMessage.status,
              existingSendername: existingMessage.sendername,
              existingIsInternal: existingIsInternalCheck,
              newStatus: newMessage.chat?.status,
              newSendername: newMessage.chat?.sendername,
              isPending: existingMessage.status === "pending",
              hasServerData: !!(newMessage.chat?.status === "internal" || newMessage.chat?.isInternalMention || newMessage.chat?.sendername),
              case1Match: !!case1Match,
              case2Match: !!case2Match,
              needsUpdate: case1Match || case2Match
            });
          }
          
          // CRITICAL: If existing message is pending and new message has server data, UPDATE it
          // OR if existing message is internal but missing sendername, UPDATE it
          // This fixes the issue where internal mentions show as "Automated" initially
          const isInternalMention = newMessage.chat?.isInternalMention || newMessage.chat?.status === "internal";
          const existingIsInternal = existingMessage.status === "internal" || existingMessage.isInternalMention === true;
          const needsUpdate = (
            // Case 1: Pending message receiving server data
            (existingMessage.status === "pending" && 
             (newMessage.chat?.status === "internal" || 
              newMessage.chat?.isInternalMention || 
              newMessage.chat?.sendername)) ||
            // Case 2: Internal message missing sendername (already has internal status)
            // Check for undefined, null, or empty string
            (existingIsInternal && 
             (!existingMessage.sendername || existingMessage.sendername === "" || existingMessage.sendername === "Automated") && 
             newMessage.chat?.sendername) ||
            // Case 3: Pending message with senderid but missing sendername (shouldn't happen, but safety check)
            // This handles cases where pending message lost sendername during initial add
            (existingMessage.status === "pending" && 
             (existingMessage.senderid || existingMessage.senderId) && 
             (!existingMessage.sendername || existingMessage.sendername === "" || existingMessage.sendername === "Automated") &&
             newMessage.chat?.sendername)
          );
          
          if (needsUpdate) {
            if (isInternalMention) {
              console.log(`🔄 [REDUX ADD MESSAGE] Updating internal mention with server data:`, {
                tempId: messageId,
                oldSendername: existingMessage.sendername,
                newSendername: newMessage.chat?.sendername,
                oldStatus: existingMessage.status,
                newStatus: newMessage.chat?.status,
                existingIsInternal,
                isPending: existingMessage.status === "pending"
              });
            }
            
            // Update the existing pending message with server data
            // Preserve important fields from existing message, but use server data for sendername, status, etc.
            const updatedMessage = {
              ...existingMessage,
              // CRITICAL: Always prioritize server sendername if available (critical for internal mentions)
              // If server doesn't have sendername but existing message does, preserve it
              // If neither has it but we have senderid, try to get from parsedUser as fallback
              sendername:
                mergeSendernameAvoidAutomatedDowngrade(existingMessage, newMessage.chat?.sendername) ||
                ((existingMessage.senderid || existingMessage.senderId) &&
                String(parsedUser?._id) === String(existingMessage.senderid ?? existingMessage.senderId)
                  ? parsedUser?.name
                  : undefined),
              isWorkflowAutomated:
                newMessage.chat?.isWorkflowAutomated !== undefined
                  ? newMessage.chat.isWorkflowAutomated
                  : existingMessage.isWorkflowAutomated,
              // Preserve performedBy — incoming socket value takes priority; fall back to existing
              performedBy: newMessage.chat?.performedBy ?? existingMessage.performedBy ?? null,
              performedByName: newMessage.chat?.performedByName ?? existingMessage.performedByName ?? null,
              // Update status from pending to internal
              status: newMessage.chat?.status || existingMessage.status,
              // Update ID if server provided a real ID
              id: newMessage.chat?.id || existingMessage.id,
              // Preserve tempId for tracking
              tempId: newMessage.chat?.tempId || existingMessage.tempId,
              // Update internal mention flags
              // CRITICAL: Set isInternalMention to true if status is "internal" or if explicitly set
              isInternalMention: newMessage.chat?.isInternalMention !== undefined 
                ? newMessage.chat.isInternalMention 
                : (newMessage.chat?.status === "internal" || existingMessage.status === "internal" 
                    ? true 
                    : (existingMessage.isInternalMention || false)),
              // CRITICAL: Always prioritize server senderAvatarColor if available
              senderAvatarColor: newMessage.chat?.senderAvatarColor || existingMessage.senderAvatarColor,
              mentionedTeamMemberId: newMessage.chat?.mentionedTeamMemberId || existingMessage.mentionedTeamMemberId,
              mentions: newMessage.chat?.mentions || existingMessage.mentions || [],
              // Keep the client's own timestamp/createdAt/time — these were captured at the
              // moment the user hit send (see sendClickedAtMs in TextBox.jsx) and are what
              // the real-time sort in this reducer keys off of. Adopting the server's value
              // here would let queue-processing latency (server createdAt is assigned when
              // the send job is dequeued, not when it was originally sent) desync this
              // message's position from the order it was actually sent in — exactly the
              // "message 3 jumps below message 6" reordering this guards against.
              timestamp: existingMessage.timestamp,
              createdAt: existingMessage.createdAt,
              time: existingMessage.time,
              // Preserve senderid (normalize camelCase from API)
              senderid: resolveMergedSenderId(newMessage.chat, existingMessage),
              // Update message body if different (shouldn't be, but just in case)
              message: newMessage.chat?.message || newMessage.chat?.body || existingMessage.message,
              body: newMessage.chat?.body || newMessage.chat?.message || existingMessage.body,
              // A media send's caption is only known to the server's push (the pending bubble has none)
              ...(newMessage.chat?.caption ? { caption: newMessage.chat.caption } : {}),            };

            // CRITICAL: Create a new array to ensure React detects the change
            // This is important for proper re-rendering when messages are updated
            const newChats = [...state.messages.chats];
            newChats[existingMessageIndex] = updatedMessage;
            state.messages.chats = newChats;
            
            if (isInternalMention) {
              console.log(`✅ [REDUX ADD MESSAGE] Updated pending internal mention successfully:`, {
                tempId: messageId,
                sendername: updatedMessage.sendername,
                status: updatedMessage.status,
                isInternalMention: updatedMessage.isInternalMention,
                arrayLength: state.messages.chats.length,
                messageIndex: existingMessageIndex
              });
            }
            
            return; // Exit after updating
          }
          
          // For non-pending messages, skip duplicate (existing behavior)
          if (isInternalMentionCheck) {
            console.log(`⏭️ [REDUX ADD MESSAGE] Skipping duplicate internal mention (not pending or no server data):`, {
              messageId,
              existingStatus: existingMessage.status,
              existingSendername: existingMessage.sendername,
              isPending: existingMessage.status === "pending"
            });
          }
          return; // Message already exists, don't add it again
        } else {
          // No duplicate found - log for debugging internal mentions
          const isInternalMentionCheck = newMessage.chat?.isInternalMention || newMessage.chat?.status === "internal";
          if (isInternalMentionCheck) {
            if (isConsoleVerbose) {
            console.log(`🔍 [REDUX ADD MESSAGE] No duplicate found for internal mention, will add new:`, {
              messageId,
              totalMessages: state.messages.chats.length,
              sampleIds: state.messages.chats.slice(-3).map(m => ({ id: m.id, tempId: m.tempId, status: m.status }))
            });
            }
          }
        }
      }

      // Additional check: For workflow messages (no senderid/clientid), also check for duplicates by content + timestamp
      // This handles cases where the same message might come through with different IDs
      //
      // Bug fix (2026-09-26, found via live group testing): a group inbound message ALWAYS has
      // no senderid (it's inbound) and no clientid (groups carry senderJid instead, per the
      // WhatsApp Groups plan -- clientid is a 1:1-contact-only field) -- so this heuristic,
      // meant for a narrow "same automated message re-emitted under two different generated
      // ids" case, was unconditionally catching every group message too. Two participants
      // sending the identical text within 5 seconds of each other (a completely normal thing to
      // happen in a group) got the second one silently dropped, even though it already had its
      // own fully unique, real WhatsApp message id and needed no content-based guessing at all --
      // confirmed by direct DB inspection that every such message was correctly persisted with a
      // distinct id; only this in-memory heuristic was discarding it. Excluding isGroup here has
      // no effect on 1:1/Meta/Instagram/Facebook: none of those ever set isGroup, so this
      // condition evaluates exactly as it always did for them.
      if (!newMessage.isGroup && !resolveChatSenderId(newMessage.chat) && !newMessage.chat?.clientid && state.messages?.chats) {
        const messageContent = newMessage.chat?.message || newMessage.chat?.body || "";
        const messageTimestamp = newMessage.chat?.timestamp || newMessage.chat?.createdAt;
        
        if (messageContent && messageTimestamp) {
          // Check if a message with the same content and very similar timestamp (within 5 seconds) already exists
          const existingDuplicate = state.messages.chats.find(chat => {
            const chatContent = chat.message || chat.body || "";
            const chatTimestamp = chat.timestamp || chat.createdAt;
            
            // Compare content (normalized)
            const contentMatches = messageContent.trim() === chatContent.trim();
            
            // Compare timestamps (within 5 seconds) - handles cases where timestamps might differ slightly
            if (contentMatches && chatTimestamp) {
              const timeDiff = Math.abs(
                new Date(messageTimestamp).getTime() - new Date(chatTimestamp).getTime()
              );
              return timeDiff < 5000; // 5 seconds tolerance
            }
            
            return false;
          });
          
          if (existingDuplicate) {
            return; // Duplicate message detected, don't add it again
          }
        }
      }

      if (!state.messages) {
        if (!newMessage.contactID) {
          return;
        }
        
        // CRITICAL: Check for template/list/catalog/interactive messages FIRST before URL detection
        // This prevents carousel template messages (which contain image URLs in JSON) from being misclassified as images
        const isTemplateFirst = newMessage.chat.type === "template" || newMessage.chat.messageType === "template";
        const isListFirst = newMessage.chat.type === "list" || newMessage.chat.messageType === "list";
        const isCatalogFirst = newMessage.chat.type === "catalog" || newMessage.chat.messageType === "catalog";
        let isInteractiveFirst = newMessage.chat.type === "interactive" || newMessage.chat.messageType === "interactive";
        
        // Detect message type from URL if messageType is not set
        // BUT: Skip detection for template/list/catalog/interactive messages to preserve their type
        let detectedMessageType = null;
        const firstMessageContent = newMessage.chat.message || newMessage.chat.body || "";
        if (!isTemplateFirst && !isListFirst && !isCatalogFirst && !isInteractiveFirst && firstMessageContent && typeof firstMessageContent === 'string' && firstMessageContent.trim()) {
          // CRITICAL: Check path-based patterns FIRST (more reliable than file extensions)
          // Check for audio path first to prevent voice messages from being detected as video
          if (firstMessageContent.match(/\/audio\//i) ||
              firstMessageContent.match(/cloudinary\.com.*\/audio\//i)) {
            detectedMessageType = "audio";
          }
          // Check for video path
          else if (firstMessageContent.match(/\/video\//i) ||
                   firstMessageContent.match(/cloudinary\.com.*\/video\//i)) {
            detectedMessageType = "video";
          }
          // Check for image path
          else if (firstMessageContent.match(/\/image\//i) ||
                   firstMessageContent.match(/cloudinary\.com.*\/image\//i)) {
            detectedMessageType = "image";
          }
          // Then check file extensions (audio before video to prevent .ogg misclassification)
          else if (firstMessageContent.match(/\.(mp3|wav|ogg|m4a|aac|flac|oga)(\?|$)/i)) {
            detectedMessageType = "audio";
          }
          else if (firstMessageContent.match(/\.(mp4|webm|mov|avi|wmv|flv|mkv)(\?|$)/i)) {
            detectedMessageType = "video";
          }
          else if (firstMessageContent.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i)) {
            detectedMessageType = "image";
          }
          // Detect document URLs
          else if (firstMessageContent.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf)(\?|$)/i)) {
            detectedMessageType = "document";
          }
          else if (firstMessageContent.match(/\/(document|documents)\//i) && firstMessageContent.match(/\.(bin|pdf|doc|docx|xls|xlsx|ppt|pptx|txt)(\?|$)/i)) {
            detectedMessageType = "document";
          }
        }
        
        // CRITICAL: Check for catalog_purchase messages - preserve them
        // Also detect by checking if body contains catalog purchase JSON structure
        let isCatalogPurchaseFirst = newMessage.chat.type === "catalog_purchase" || newMessage.chat.messageType === "catalog_purchase";
        if (!isCatalogPurchaseFirst && firstMessageContent) {
          try {
            const parsedBody = typeof firstMessageContent === 'string' ? JSON.parse(firstMessageContent) : firstMessageContent;
            // Check if it has the structure of a catalog purchase message
            if (parsedBody && typeof parsedBody === 'object' && 
                (parsedBody.productId || parsedBody.catalogId || parsedBody.allProducts || parsedBody.items)) {
              isCatalogPurchaseFirst = true;
            }
          } catch (e) {
            // Not JSON, ignore
          }
        }
        
        // CRITICAL: Check for interactive messages (button messages) - detect from body JSON if not already set
        if (!isInteractiveFirst && firstMessageContent) {
          try {
            const parsedBody = typeof firstMessageContent === 'string' ? JSON.parse(firstMessageContent) : firstMessageContent;
            // Check if it has the structure of an interactive button message
            if (parsedBody && typeof parsedBody === 'object' && 
                parsedBody.type === "button" && Array.isArray(parsedBody.buttons)) {
              isInteractiveFirst = true;
            }
          } catch (e) {
            // Not JSON, ignore
          }
        }
        
        // CRITICAL: Normalize the first message to ensure messageType, type, and message fields are set
        // First determine the normalized messageType
        // CRITICAL: Preserve template/list/catalog/interactive types - don't override with detected type
        const normalizedMessageType = isCatalogPurchaseFirst ? "catalog_purchase" :
                      (isTemplateFirst ? "template" :
                      (isListFirst ? "list" :
                      (isCatalogFirst ? "catalog" :
                      (isInteractiveFirst ? "interactive" :
                      (detectedMessageType || 
                      (newMessage.chat.messageType && newMessage.chat.messageType !== "chat" ? newMessage.chat.messageType : null) ||
                      (newMessage.chat.type && newMessage.chat.type !== "chat" ? newMessage.chat.type : "text"))))));
        
        // For Facebook/Instagram media, prefer url so image/video/audio/document display correctly
        const isMediaFirst = normalizedMessageType && ["image", "video", "audio", "document"].includes(normalizedMessageType);
        const messageFirst = isMediaFirst && newMessage.chat.url ? newMessage.chat.url : (newMessage.chat.message || newMessage.chat.body || "");

        const normalizedChat = {
          ...newMessage.chat,
          // CRITICAL: Always prioritize detected message type if available (it's more accurate)
          // Override messageType even if it's already set to "chat" or "text" when we detect a media URL
          // BUT: Preserve catalog_purchase messages
          messageType: normalizedMessageType,
          message: messageFirst,
          // ChatBody checks for type === "chat" to render messages
          // Use the normalized messageType, not the original one
          type: (normalizedMessageType && ["image", "video", "audio", "document", "location", "contacts", "text", "list", "catalog", "catalog_purchase", "interactive"].includes(normalizedMessageType))
            ? "chat"
            : (newMessage.chat.type || "chat"),
          // CRITICAL: Ensure id exists for React key prop
          id: newMessage.chat.id || newMessage.chat._id || newMessage.chat.tempId || `msg-${Date.now()}-${Math.random()}`,
          senderid: resolveChatSenderId(newMessage.chat),
          // CRITICAL: Ensure timestamp is preserved for sorting (convert to ISO string for Redux serialization)
          // Workflow-bot messages often carry only `time`, not `timestamp`/`createdAt` — fall back to it.
          timestamp: toISOString(newMessage.chat.timestamp || newMessage.chat.createdAt || newMessage.chat.time),
          // CRITICAL: Preserve createdAt for proper message ordering (convert to ISO string for Redux serialization)
          createdAt: toISOString(newMessage.chat.createdAt || newMessage.chat.timestamp || newMessage.chat.time),
          // CRITICAL: Preserve time field with the original timestamp value for display
          // Use the original time if available, otherwise use timestamp or createdAt
          // CRITICAL: Ensure time is not duplicated - only set if it doesn't already exist or needs conversion
          time: newMessage.chat.time 
            ? (typeof newMessage.chat.time === 'string' && newMessage.chat.time.includes('T') 
                ? newMessage.chat.time 
                : toISOString(newMessage.chat.time))
            : toISOString(newMessage.chat.timestamp || newMessage.chat.createdAt),
          // CRITICAL: Preserve reply information (replySenderName, replyBody, replyType, replyId)
          replySenderName: newMessage.chat.replySenderName || newMessage.replySenderName,
          replyBody: newMessage.chat.replyBody || newMessage.replyBody,
          replyType: newMessage.chat.replyType || newMessage.replyType,
          replyId: newMessage.chat.replyId || newMessage.chat.replyTo || newMessage.replyId,
          // CRITICAL: For assignment messages, preserve toName and to for ChatBody.jsx display
          ...(newMessage.chat.type === "assignment" && {
            to: newMessage.chat.to !== undefined ? newMessage.chat.to : (newMessage.to !== undefined ? newMessage.to : null),
            toName: newMessage.chat.toName !== undefined ? newMessage.chat.toName : (newMessage.toName !== undefined ? newMessage.toName : null),
            date: toISOString(newMessage.chat.date || newMessage.date || newMessage.chat.timestamp || newMessage.chat.createdAt),
          }),
        };
        state.messages = {
          contactID: newMessage.contactID,
          chats: [normalizedChat],
          platform: newMessage.platform ?? "whatsapp",
          contactName: newMessage.contactName ?? newMessage.name ?? "",
        };
        return;
      }

      if (state.messages.contactID === newMessage.contactID) {
        // parsedUser and currentUserId are already defined at the top of the function

        const existingMessageIndex = state.messages.chats.findIndex(
          (message) => {
            if (newMessage.chat.id && message.id === newMessage.chat.id) {
              return true;
            }
            if (
              newMessage.chat.tempId &&
              message.tempId === newMessage.chat.tempId
            ) {
              return true;
            }
            if (
              (message.status === "pending" || message.status === "sent") &&
              message.body === newMessage.chat.body
            ) {
              const messageTime = new Date(message.timestamp).getTime();
              const newMessageTime = new Date(
                newMessage.chat.timestamp
              ).getTime();
              return Math.abs(messageTime - newMessageTime) < 120000;
            }
            return false;
          }
        );

        if (existingMessageIndex !== -1) {
          const existingMessage = state.messages.chats[existingMessageIndex];
          const currentStatus = existingMessage.status;
          const newStatus = newMessage.chat.status;
          
          // CRITICAL: Log when existing message is found for debugging
          if (currentStatus === "pending" && newStatus === "internal") {
            console.log(`🔍 [REDUX STATUS UPDATE] Found existing pending message:`, {
              tempId: existingMessage.tempId || existingMessage.id,
              existingSendername: existingMessage.sendername,
              existingSenderid: existingMessage.senderid,
              existingStatus: currentStatus,
              newStatus: newStatus,
              serverSendername: newMessage.chat.sendername,
              serverSenderid: newMessage.chat.senderid,
              serverSenderAvatarColor: newMessage.chat.senderAvatarColor
            });
          }

          const precedence = {
            pending: 0,
            sent: 1,
            delivered: 2,
            read: 3,
            seen: 3,
            internal: -1, // CRITICAL: Internal mentions should not be updated by status precedence
          };

          // CRITICAL: Handle status updates, but also handle internal mentions specially
          // Internal mentions should update from pending to internal, but not through normal precedence
          const isInternalStatusUpdate = newStatus === "internal" && currentStatus === "pending";
          const isNormalStatusUpdate = (precedence[newStatus] || 0) > (precedence[currentStatus] || 0);
          
          if (isInternalStatusUpdate || isNormalStatusUpdate) {
            // Keep existing message data, just update status
            // CRITICAL: Preserve createdAt for proper message ordering
            // CRITICAL: Preserve both tempId and id so status updates can match either one
            // CRITICAL: Preserve sendername if new message has one (especially for internal mentions)
            // CRITICAL: For internal status updates, ALWAYS use new message sendername if available
            // This ensures pending messages that lost sendername get it back when updated to internal
            let updatedSendername;
            if (isInternalStatusUpdate && newMessage.chat.sendername) {
              const inc = newMessage.chat.sendername;
              updatedSendername =
                inc === "Automated" &&
                existingMessage.sendername &&
                existingMessage.sendername !== "Automated" &&
                (existingMessage.senderid || existingMessage.senderId)
                  ? existingMessage.sendername
                  : inc;
            } else {
              const merged = mergeSendernameAvoidAutomatedDowngrade(
                existingMessage,
                newMessage.chat.sendername
              );
              const exSid = existingMessage.senderid ?? existingMessage.senderId;
              updatedSendername =
                merged ||
                (exSid &&
                String(parsedUser?._id) === String(exSid)
                  ? parsedUser?.name
                  : undefined);
            }
            
            state.messages.chats[existingMessageIndex] = {
              ...existingMessage,
              status: newStatus,
              id: newMessage.chat.id || existingMessage.id,
              // Preserve tempId if it exists (either from existing message or new message)
              tempId: newMessage.chat.tempId || existingMessage.tempId,
              // CRITICAL: Use the computed sendername (prioritizes server data for internal updates)
              sendername: updatedSendername,
              senderid: resolveMergedSenderId(newMessage.chat, existingMessage),
              // CRITICAL: Update isInternalMention if status is changing to 'internal'
              isInternalMention: (newStatus === "internal" || newMessage.chat.isInternalMention)
                ? true
                : existingMessage.isInternalMention,
              // CRITICAL: Always prioritize server senderAvatarColor for internal updates
              senderAvatarColor: (isInternalStatusUpdate && newMessage.chat.senderAvatarColor)
                ? newMessage.chat.senderAvatarColor
                : (newMessage.chat.senderAvatarColor || existingMessage.senderAvatarColor),
              // timestamp intentionally left as existingMessage.timestamp — it's a sort key
              // (see the resort below); adopting the re-emitted status event's time here
              // would desync it from send order and reshuffle the list on the next resort.
              timestamp: existingMessage.timestamp,
              // Same reasoning as timestamp above — always keep the existing createdAt once
              // set, rather than blending in the server's value (even the earlier-of-the-two
              // logic this used to have could still adopt a server createdAt that doesn't
              // reflect true send order, since server createdAt is assigned when its queued
              // send job is processed, not when the user actually hit send).
              createdAt: existingMessage.createdAt || newMessage.chat.createdAt || existingMessage.timestamp,
              // Preserve performedBy — incoming value takes priority over existing (covers status-update re-emits)
              performedBy: newMessage.chat?.performedBy ?? existingMessage.performedBy ?? null,
              performedByName: newMessage.chat?.performedByName ?? existingMessage.performedByName ?? null,
            };
            
            // CRITICAL: Log internal status updates for debugging
            if (isInternalStatusUpdate) {
              console.log(`🔄 [REDUX STATUS UPDATE] Updated pending to internal:`, {
                tempId: existingMessage.tempId || existingMessage.id,
                oldSendername: existingMessage.sendername,
                newSendername: updatedSendername,
                oldStatus: currentStatus,
                newStatus: newStatus,
                serverSendername: newMessage.chat.sendername,
                serverSenderAvatarColor: newMessage.chat.senderAvatarColor,
                finalSenderAvatarColor: newMessage.chat.senderAvatarColor || existingMessage.senderAvatarColor,
                existingSenderid: existingMessage.senderid,
                newSenderid: newMessage.chat.senderid
              });
            }
          } else {
            // CRITICAL: Log when status update is skipped for internal mentions
            if (newStatus === "internal" && currentStatus !== "pending") {
              console.log(`⚠️ [REDUX STATUS UPDATE] Skipped status update for internal mention:`, {
                tempId: existingMessage.tempId || existingMessage.id,
                currentStatus,
                newStatus,
                existingSendername: existingMessage.sendername,
                serverSendername: newMessage.chat.sendername,
                isInternalStatusUpdate: false,
                isNormalStatusUpdate: false
              });
            }
          }
        } else {
          // Check if this is a message from the current user
          const incomingSenderId = resolveChatSenderId(newMessage.chat);
          const isFromCurrentUser =
            incomingSenderId != null &&
            currentUserId != null &&
            String(incomingSenderId) === String(currentUserId);

          if (isFromCurrentUser) {
            // Only add sendername for messages from current user
            // CRITICAL: Check for template/list/catalog/interactive messages FIRST before URL detection
            // This prevents carousel template messages (which contain image URLs in JSON) from being misclassified as images
            const isTemplateFromMe = newMessage.chat.type === "template" || newMessage.chat.messageType === "template";
            const isListFromMe = newMessage.chat.type === "list" || newMessage.chat.messageType === "list";
            const isCatalogFromMe = newMessage.chat.type === "catalog" || newMessage.chat.messageType === "catalog";
            let isInteractiveFromMe = newMessage.chat.type === "interactive" || newMessage.chat.messageType === "interactive";
            
            // Detect message type from URL if messageType is not set
            // BUT: Skip detection for template/list/catalog/interactive messages to preserve their type
            let detectedMessageType = null;
            const messageContent = newMessage.chat.message || newMessage.chat.body || "";
            if (!isTemplateFromMe && !isListFromMe && !isCatalogFromMe && !isInteractiveFromMe && messageContent && typeof messageContent === 'string' && messageContent.trim()) {
              // CRITICAL: Check path-based patterns FIRST (more reliable than file extensions)
              // Check for audio path first to prevent voice messages from being detected as video
              if (messageContent.match(/\/audio\//i) ||
                  messageContent.match(/cloudinary\.com.*\/audio\//i)) {
                detectedMessageType = "audio";
              }
              // Check for video path
              else if (messageContent.match(/\/video\//i) ||
                       messageContent.match(/cloudinary\.com.*\/video\//i)) {
                detectedMessageType = "video";
              }
              // Check for image path
              else if (messageContent.match(/\/image\//i) ||
                       messageContent.match(/cloudinary\.com.*\/image\//i)) {
                detectedMessageType = "image";
              }
              // Then check file extensions (audio before video to prevent .ogg misclassification)
              else if (messageContent.match(/\.(mp3|wav|ogg|m4a|aac|flac|oga)(\?|$)/i)) {
                detectedMessageType = "audio";
              }
              else if (messageContent.match(/\.(mp4|webm|mov|avi|wmv|flv|mkv)(\?|$)/i)) {
                detectedMessageType = "video";
              }
              else if (messageContent.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i)) {
                detectedMessageType = "image";
              }
              // Detect document URLs
              else if (messageContent.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf)(\?|$)/i)) {
                detectedMessageType = "document";
              }
              else if (messageContent.match(/\/(document|documents)\//i) && messageContent.match(/\.(bin|pdf|doc|docx|xls|xlsx|ppt|pptx|txt)(\?|$)/i)) {
                detectedMessageType = "document";
              }
            }
            
            // CRITICAL: Check for catalog_purchase messages - preserve them
            // Also detect by checking if body contains catalog purchase JSON structure
            let isCatalogPurchaseFromMe = newMessage.chat.type === "catalog_purchase" || newMessage.chat.messageType === "catalog_purchase";
            if (!isCatalogPurchaseFromMe && messageContent) {
              try {
                const parsedBody = typeof messageContent === 'string' ? JSON.parse(messageContent) : messageContent;
                // Check if it has the structure of a catalog purchase message
                if (parsedBody && typeof parsedBody === 'object' && 
                    (parsedBody.productId || parsedBody.catalogId || parsedBody.allProducts || parsedBody.items)) {
                  isCatalogPurchaseFromMe = true;
                }
              } catch (e) {
                // Not JSON, ignore
              }
            }
            
            // CRITICAL: Check for interactive messages (button messages) - detect from body JSON if not already set
            if (!isInteractiveFromMe && messageContent) {
              try {
                const parsedBody = typeof messageContent === 'string' ? JSON.parse(messageContent) : messageContent;
                // Check if it has the structure of an interactive button message
                if (parsedBody && typeof parsedBody === 'object' && 
                    parsedBody.type === "button" && Array.isArray(parsedBody.buttons)) {
                  isInteractiveFromMe = true;
                }
              } catch (e) {
                // Not JSON, ignore
              }
            }
            
            // First determine the normalized messageType
            // CRITICAL: Preserve template/list/catalog/interactive types - don't override with detected type
            const normalizedMessageTypeFromMe = isCatalogPurchaseFromMe ? "catalog_purchase" :
                          (isTemplateFromMe ? "template" :
                          (isListFromMe ? "list" :
                          (isCatalogFromMe ? "catalog" :
                          (isInteractiveFromMe ? "interactive" :
                          (detectedMessageType || 
                          (newMessage.chat.messageType && newMessage.chat.messageType !== "chat" ? newMessage.chat.messageType : null) ||
                          (newMessage.chat.type && newMessage.chat.type !== "chat" ? newMessage.chat.type : "text"))))));
            
              // For Facebook/Instagram media, prefer url so image/video/audio/document display correctly
              const isMediaFromMe = normalizedMessageTypeFromMe && ["image", "video", "audio", "document"].includes(normalizedMessageTypeFromMe);
              const messageFromMe = isMediaFromMe && newMessage.chat.url ? newMessage.chat.url : (newMessage.chat.message || newMessage.chat.body || "");

              if (newMessage.chat.status === "pending") {
                console.log(`📝 [REDUX ADD MESSAGE] Adding pending message from current user:`, {
                  tempId: newMessage.chat.tempId || newMessage.chat.id,
                  sendername: newMessage.chat.sendername,
                  senderid: resolveChatSenderId(newMessage.chat),
                  parsedUserName: parsedUser?.name,
                  status: newMessage.chat.status
                });
              }

              const messageToAdd = {
              ...newMessage.chat,
              // Map body to message for ChatCard compatibility
              message: messageFromMe,
              // CRITICAL: Always prioritize detected message type if available (it's more accurate)
              // Override messageType even if it's already set to "chat" or "text" when we detect a media URL
              // BUT: Preserve catalog_purchase messages
              messageType: normalizedMessageTypeFromMe,
              // CRITICAL: Ensure type is "chat" for ChatBody to render
              // Use the normalized messageType we just set above
              type: (normalizedMessageTypeFromMe && ["image", "video", "audio", "document", "location", "contacts", "text", "list", "catalog", "catalog_purchase", "interactive"].includes(normalizedMessageTypeFromMe))
                ? "chat"
                : (newMessage.chat.type || "chat"),
              // CRITICAL: Ensure id exists for React key prop
              id: newMessage.chat.id || newMessage.chat._id || newMessage.chat.tempId || `msg-${Date.now()}-${Math.random()}`,
              // CRITICAL: Preserve tempId for status update matching (use id as fallback if tempId not provided)
              tempId: newMessage.chat.tempId || newMessage.chat.id || newMessage.chat._id,
              senderid: resolveChatSenderId(newMessage.chat),
              // CRITICAL: Preserve sendername from newMessage.chat if it exists (especially for pending messages)
              // This ensures internal mentions keep their sendername from the moment they're added
              // CRITICAL: For pending messages with senderid, ALWAYS set sendername from parsedUser if not provided
              // This prevents pending messages from losing sendername on initial add
              sendername: newMessage.chat.sendername || 
                         (newMessage.chat.status === "pending" && resolveChatSenderId(newMessage.chat) && parsedUser?._id && String(parsedUser._id) === String(resolveChatSenderId(newMessage.chat)) ? parsedUser?.name : undefined) ||
                         parsedUser?.name,
              isFromMe: true,
              // CRITICAL: Preserve createdAt for proper message ordering (convert to ISO string for Redux serialization)
              // If not provided, use timestamp as fallback
              createdAt: toISOString(newMessage.chat.createdAt || newMessage.chat.timestamp),
              // CRITICAL: Ensure timestamp is preserved for sorting (convert to ISO string for Redux serialization)
              timestamp: toISOString(newMessage.chat.timestamp || newMessage.chat.createdAt),
              // CRITICAL: Preserve time field with the original timestamp value for display
              // Use the original time if available, otherwise use timestamp or createdAt
              // CRITICAL: If time is already an ISO string (from socket handler), don't convert it again
              time: newMessage.chat.time 
                ? (typeof newMessage.chat.time === 'string' && (newMessage.chat.time.includes('T') || newMessage.chat.time.match(/^\d{4}-\d{2}-\d{2}/))
                    ? newMessage.chat.time  // Already an ISO string, use as-is
                    : toISOString(newMessage.chat.time))  // Convert if needed
                : toISOString(newMessage.chat.timestamp || newMessage.chat.createdAt),
              // CRITICAL: For assignment messages, preserve toName and to for ChatBody.jsx display
              ...(newMessage.chat.type === "assignment" && {
                to: newMessage.chat.to !== undefined ? newMessage.chat.to : (newMessage.to !== undefined ? newMessage.to : null),
                toName: newMessage.chat.toName !== undefined ? newMessage.chat.toName : (newMessage.toName !== undefined ? newMessage.toName : null),
                date: toISOString(newMessage.chat.date || newMessage.date || newMessage.chat.timestamp || newMessage.chat.createdAt),
              }),
            };
            
            // CRITICAL: Log pending message addition for debugging
            if (newMessage.chat.status === "pending") {
              console.log(`✅ [REDUX ADD MESSAGE] Added pending message to state:`, {
                tempId: messageToAdd.tempId || messageToAdd.id,
                sendername: messageToAdd.sendername,
                senderid: messageToAdd.senderid,
                status: messageToAdd.status,
                totalMessages: state.messages.chats.length
              });
            }
            
            // Create new array instead of mutating
            state.messages.chats = [...state.messages.chats, messageToAdd];
          } else {
            let senderName = newMessage.chat.sendername;
            // CRITICAL: Check for internal mentions first - never override sendername for internal mentions
            const isInternalMention = newMessage.chat.isInternalMention || newMessage.chat.status === "internal";
            
            // If clientid or clientname exists, don't set sendername - keep it undefined/null
            // This ensures client messages are properly identified as from client (they use clientname)
            if (newMessage.chat.clientid || newMessage.chat.clientname) {
              // Don't set sendername for client messages - let clientname handle the display
              senderName = undefined;
            } else if (!resolveChatSenderId(newMessage.chat) && !newMessage.chat.clientid && !newMessage.chat.clientname && !isInternalMention) {
              const msgPlatform = newMessage.platform ?? state.messages?.platform ?? "whatsapp";
              if (msgPlatform === "whatsapp" || msgPlatform === "qr") {
                if (senderName === "Automated") senderName = undefined;
                if (!senderName || senderName === "" || senderName === "Unknown") {
                  senderName = undefined;
                }
              } else if (!senderName || senderName === "" || senderName === "Unknown") {
                senderName = "Automated";
              }
            }
            // CRITICAL: For internal mentions, always preserve sendername from newMessage.chat.sendername
            // Never override it with "Automated"
            if (isInternalMention) {
              if (newMessage.chat.sendername) {
                senderName = newMessage.chat.sendername;
              } else if (resolveChatSenderId(newMessage.chat)) {
                // CRITICAL: If sendername is missing but senderid exists, log a warning
                // The server should include sendername in socket events for internal mentions
                // For now, keep senderName as undefined and let the UI handle it
                // The message will show correctly after reload when fetched from DB
                console.warn(`⚠️ [REDUX] Internal mention message missing sendername:`, {
                  messageId: newMessage.chat.id || newMessage.chat.tempId,
                  senderid: resolveChatSenderId(newMessage.chat),
                  isInternalMention: true
                });
              }
            }
            // CRITICAL: Check for template/list/catalog/interactive messages FIRST before URL detection
            // This prevents carousel template messages (which contain image URLs in JSON) from being misclassified as images
            const isTemplateOther = newMessage.chat.type === "template" || newMessage.chat.messageType === "template";
            const isListOther = newMessage.chat.type === "list" || newMessage.chat.messageType === "list";
            const isCatalogOther = newMessage.chat.type === "catalog" || newMessage.chat.messageType === "catalog";
            let isInteractiveOther = newMessage.chat.type === "interactive" || newMessage.chat.messageType === "interactive";
            
            // Detect message type from URL if messageType is not set (for non-current user messages)
            // BUT: Skip detection for template/list/catalog/interactive messages to preserve their type
            let detectedMessageTypeOther = null;
            const messageContentOther = newMessage.chat.message || newMessage.chat.body || "";
            if (!isTemplateOther && !isListOther && !isCatalogOther && !isInteractiveOther && messageContentOther && typeof messageContentOther === 'string' && messageContentOther.trim()) {
              // CRITICAL: Check path-based patterns FIRST (more reliable than file extensions)
              // Check for audio path first to prevent voice messages from being detected as video
              if (messageContentOther.match(/\/audio\//i) ||
                  messageContentOther.match(/cloudinary\.com.*\/audio\//i)) {
                detectedMessageTypeOther = "audio";
              }
              // Check for video path
              else if (messageContentOther.match(/\/video\//i) ||
                       messageContentOther.match(/cloudinary\.com.*\/video\//i)) {
                detectedMessageTypeOther = "video";
              }
              // Check for image path
              else if (messageContentOther.match(/\/image\//i) ||
                       messageContentOther.match(/cloudinary\.com.*\/image\//i)) {
                detectedMessageTypeOther = "image";
              }
              // Then check file extensions (audio before video to prevent .ogg misclassification)
              else if (messageContentOther.match(/\.(mp3|wav|ogg|m4a|aac|flac|oga)(\?|$)/i)) {
                detectedMessageTypeOther = "audio";
              }
              else if (messageContentOther.match(/\.(mp4|webm|mov|avi|wmv|flv|mkv)(\?|$)/i)) {
                detectedMessageTypeOther = "video";
              }
              else if (messageContentOther.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i)) {
                detectedMessageTypeOther = "image";
              }
              // Detect document URLs
              else if (messageContentOther.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf)(\?|$)/i)) {
                detectedMessageTypeOther = "document";
              }
              else if (messageContentOther.match(/\/(document|documents)\//i) && messageContentOther.match(/\.(bin|pdf|doc|docx|xls|xlsx|ppt|pptx|txt)(\?|$)/i)) {
                detectedMessageTypeOther = "document";
              }
            }
            
            // CRITICAL: Check for catalog_purchase messages - preserve them
            // Also detect by checking if body contains catalog purchase JSON structure
            let isCatalogPurchaseOther = newMessage.chat.type === "catalog_purchase" || newMessage.chat.messageType === "catalog_purchase";
            if (!isCatalogPurchaseOther && messageContentOther) {
              try {
                const parsedBody = typeof messageContentOther === 'string' ? JSON.parse(messageContentOther) : messageContentOther;
                // Check if it has the structure of a catalog purchase message
                if (parsedBody && typeof parsedBody === 'object' && 
                    (parsedBody.productId || parsedBody.catalogId || parsedBody.allProducts || parsedBody.items)) {
                  isCatalogPurchaseOther = true;
                }
              } catch (e) {
                // Not JSON, ignore
              }
            }
            
            // CRITICAL: Check for interactive messages (button messages) - detect from body JSON if not already set
            if (!isInteractiveOther && messageContentOther) {
              try {
                const parsedBody = typeof messageContentOther === 'string' ? JSON.parse(messageContentOther) : messageContentOther;
                // Check if it has the structure of an interactive button message
                if (parsedBody && typeof parsedBody === 'object' && 
                    parsedBody.type === "button" && Array.isArray(parsedBody.buttons)) {
                  isInteractiveOther = true;
                }
              } catch (e) {
                // Not JSON, ignore
              }
            }
            
            // First determine the normalized messageType
            // CRITICAL: Preserve template/list/catalog/interactive types - don't override with detected type
            const normalizedMessageTypeOther = isCatalogPurchaseOther ? "catalog_purchase" :
                          (isTemplateOther ? "template" :
                          (isListOther ? "list" :
                          (isCatalogOther ? "catalog" :
                          (isInteractiveOther ? "interactive" :
                          (newMessage.chat.messageType || 
                          detectedMessageTypeOther ||
                          (newMessage.chat.type && newMessage.chat.type !== "chat" ? newMessage.chat.type : "text"))))));
            
            // For Facebook/Instagram media, prefer url so image/video/audio/document display correctly
            const isMediaOther = normalizedMessageTypeOther && ["image", "video", "audio", "document"].includes(normalizedMessageTypeOther);
            const messageOther = isMediaOther && newMessage.chat.url ? newMessage.chat.url : (newMessage.chat.message || newMessage.chat.body || "");

            const messageToAdd = {
              ...newMessage.chat,
              // Map body to message for ChatCard compatibility
              message: messageOther,
              // CRITICAL: Preserve messageType for proper rendering
              // Use detected message type if available, otherwise use type if not "chat", otherwise default to "text"
              // BUT: Preserve catalog_purchase messages
              messageType: normalizedMessageTypeOther,
              // CRITICAL: Ensure type is "chat" for ChatBody to render
              // Use the normalized messageType we just set above
              type: (normalizedMessageTypeOther && ["image", "video", "audio", "document", "location", "contacts", "text", "list", "catalog", "catalog_purchase", "interactive"].includes(normalizedMessageTypeOther))
                ? "chat"
                : (newMessage.chat.type || "chat"),
              // CRITICAL: Ensure id exists for React key prop
              id: newMessage.chat.id || newMessage.chat._id || newMessage.chat.tempId || `msg-${Date.now()}-${Math.random()}`,
              // CRITICAL: Only set sendername if it's computed AND not a client message
              // For client messages, keep sendername undefined so clientname is used
              // CRITICAL: Always prefer newMessage.chat.sendername if it exists (from server)
              // This ensures internal mentions preserve their sendername correctly
              // CRITICAL: For internal mentions, ALWAYS use sendername from server if available
              // If server sendername is missing, fallback to computed senderName
              // CRITICAL: For pending messages with senderid (from current user), preserve sendername
              // This ensures internal mentions that haven't been detected yet still keep their sendername
              sendername: (newMessage.chat.clientid || newMessage.chat.clientname) 
                ? undefined 
                : (isInternalMention 
                    ? (() => {
                        const finalSendername = newMessage.chat.sendername || senderName;
                        console.log(`🔍 [REDUX ADD MESSAGE] Internal mention sendername:`, {
                          messageId: newMessage.chat.id || newMessage.chat.tempId,
                          serverSendername: newMessage.chat.sendername,
                          computedSenderName: senderName,
                          finalSendername,
                          isInternalMention: true,
                          status: newMessage.chat.status,
                          senderid: resolveChatSenderId(newMessage.chat)
                        });
                        return finalSendername;
                      })()
                    : (
                        // CRITICAL: For pending messages with senderid (from current user), preserve sendername
                        // This ensures internal mentions that haven't been detected yet still keep their sendername
                        (newMessage.chat.status === "pending" && resolveChatSenderId(newMessage.chat) && newMessage.chat.sendername)
                          ? newMessage.chat.sendername
                          : (newMessage.chat.sendername || (senderName !== undefined && senderName !== null && senderName !== "" ? senderName : undefined))
                      )),
              senderid: resolveChatSenderId(newMessage.chat),
              // CRITICAL: Only set clientname if server sent null/undefined AND we have clientid
              // Preserve server data - only fill in missing values, don't override
              clientname: (newMessage.chat.clientname !== null && newMessage.chat.clientname !== undefined)
                ? newMessage.chat.clientname  // Use server value if it exists (even if empty string)
                : (newMessage.chat.clientid ? (
                    newMessage.name || 
                    newMessage.contactName || 
                    (newMessage.contactInfo?.name) ||
                    undefined
                  ) : undefined),
              // Only set isFromMe to true if it's from current user OR if it's truly automated (no clientid/clientname)
              isFromMe:
                isFromCurrentUser ||
                ((senderName === "Automated" || newMessage.chat.isWorkflowAutomated === true) &&
                  !newMessage.chat.clientid &&
                  !newMessage.chat.clientname),
              // CRITICAL: Preserve createdAt for proper message ordering (convert to ISO string for Redux serialization)
              // If not provided, use timestamp as fallback
              createdAt: toISOString(newMessage.chat.createdAt || newMessage.chat.timestamp),
              // CRITICAL: Ensure timestamp is preserved for sorting (convert to ISO string for Redux serialization)
              timestamp: toISOString(newMessage.chat.timestamp || newMessage.chat.createdAt),
              // CRITICAL: Preserve time field with the original timestamp value for display
              // Use the original time if available, otherwise use timestamp or createdAt
              // CRITICAL: If time is already an ISO string (from socket handler), don't convert it again
              time: newMessage.chat.time 
                ? (typeof newMessage.chat.time === 'string' && (newMessage.chat.time.includes('T') || newMessage.chat.time.match(/^\d{4}-\d{2}-\d{2}/))
                    ? newMessage.chat.time  // Already an ISO string, use as-is
                    : toISOString(newMessage.chat.time))  // Convert if needed
                : toISOString(newMessage.chat.timestamp || newMessage.chat.createdAt),
              // CRITICAL: For assignment messages, preserve toName and to for ChatBody.jsx display
              ...(newMessage.chat.type === "assignment" && {
                to: newMessage.chat.to !== undefined ? newMessage.chat.to : (newMessage.to !== undefined ? newMessage.to : null),
                toName: newMessage.chat.toName !== undefined ? newMessage.chat.toName : (newMessage.toName !== undefined ? newMessage.toName : null),
                date: toISOString(newMessage.chat.date || newMessage.date || newMessage.chat.timestamp || newMessage.chat.createdAt),
              }),
              // CRITICAL: Preserve internal mention fields for proper styling
              isInternalMention: newMessage.chat.isInternalMention || false,
              senderAvatarColor: newMessage.chat.senderAvatarColor || null,
              mentionedTeamMemberId: newMessage.chat.mentionedTeamMemberId || null,
              mentions: newMessage.chat.mentions || [],
            };
            
            // CRITICAL: Log internal mention message being added to Redux
            if (isInternalMention) {
              console.log(`✅ [REDUX ADD MESSAGE] Adding internal mention to state:`, {
                messageId: messageToAdd.id || messageToAdd.tempId,
                sendername: messageToAdd.sendername,
                senderid: messageToAdd.senderid,
                status: messageToAdd.status,
                isInternalMention: messageToAdd.isInternalMention,
                senderAvatarColor: messageToAdd.senderAvatarColor,
                totalMessagesBefore: state.messages.chats.length
              });
            }
            
            // Create new array instead of mutating
            state.messages.chats = [...state.messages.chats, messageToAdd];
            
            if (isInternalMention) {
              console.log(`✅ [REDUX ADD MESSAGE] Internal mention added successfully. Total messages:`, {
                totalMessagesAfter: state.messages.chats.length,
                messageId: messageToAdd.id || messageToAdd.tempId,
                sendername: messageToAdd.sendername
              });
            }
          }
        }

        // Sort chats by creating new array with secondary sort key
        const beforeSortCount = state.messages.chats.length;
        // Create a map with original indices to preserve insertion order for tie-breaking
        const chatsWithIndex = state.messages.chats.map((chat, index) => ({
          ...chat,
          _originalIndex: index
        }));
        
        state.messages.chats = chatsWithIndex.slice().sort((a, b) => {
          // Use createdAt if available, otherwise fallback to timestamp
          const timeA = (a.createdAt ? new Date(a.createdAt) : (a.timestamp ? new Date(a.timestamp) : new Date(0))).getTime();
          const timeB = (b.createdAt ? new Date(b.createdAt) : (b.timestamp ? new Date(b.timestamp) : new Date(0))).getTime();

          // Primary sort: by time (createdAt or timestamp)
          if (timeA !== timeB) {
            return timeA - timeB;
          }

          // Secondary sort (tie-break): original array index, i.e. insertion/arrival order.
          // Deliberately NOT falling back to `timestamp` here — updateMessageStatus does not
          // mutate it anymore, but messages that share a createdAt (e.g. several sent within
          // the same WhatsApp-reported second) must keep the order they arrived in rather than
          // being re-ordered by any other mutable field.
          if (a._originalIndex !== undefined && b._originalIndex !== undefined) {
            return a._originalIndex - b._originalIndex;
          }

          // Fallback: use message ID if timestamps are identical
          return (a.id || '').localeCompare(b.id || '');
        }).map(({ _originalIndex, ...chat }) => chat); // Remove _originalIndex after sorting
        
        // Keep chat header in sync: platform and contactName for correct icon/name (e.g. Facebook vs WhatsApp)
        if (newMessage.platform !== undefined && newMessage.platform !== null) {
          state.messages.platform = newMessage.platform;
        }
        if (newMessage.contactName !== undefined && newMessage.contactName !== null) {
          state.messages.contactName = newMessage.contactName;
        } else if (newMessage.name !== undefined && newMessage.name !== null) {
          state.messages.contactName = newMessage.name;
        }
        
        // Debug: Verify message still exists after sorting
        if (beforeSortCount > 0) {
          const workflowMessagesAfterSort = state.messages.chats.filter(msg => 
            !msg.senderid && !msg.clientid && (msg.sendername === "Automated" || msg.isWorkflowAutomated === true)
          );
          if (workflowMessagesAfterSort.length > 0) {
            console.log("✅ After sorting - workflow messages count:", workflowMessagesAfterSort.length, "Latest:", {
              id: workflowMessagesAfterSort[workflowMessagesAfterSort.length - 1]?.id,
              type: workflowMessagesAfterSort[workflowMessagesAfterSort.length - 1]?.type,
              messageType: workflowMessagesAfterSort[workflowMessagesAfterSort.length - 1]?.messageType,
              message: workflowMessagesAfterSort[workflowMessagesAfterSort.length - 1]?.message ?
                (typeof workflowMessagesAfterSort[workflowMessagesAfterSort.length - 1].message === 'string' ?
                  workflowMessagesAfterSort[workflowMessagesAfterSort.length - 1].message.substring(0, 30) :
                  typeof workflowMessagesAfterSort[workflowMessagesAfterSort.length - 1].message) :
                'no message'
            });
          }
        }
      }

      // IDB snapshot is now stale — this message exists in Redux but not in the cached
      // API response. Invalidate so the next contact switch + return triggers a fresh fetch.
      const addedContactId = newMessage.contactID || newMessage.contactId;
      if (addedContactId) {
        invalidate(STORES.MESSAGES, String(addedContactId)); // fire-and-forget
        messageCache.delete(String(addedContactId));
      }
    },
    updateMessageStatus(state, action) {
      // CRITICAL: Log at the very start to verify reducer is being called
      console.log(`🚀 [REDUX STATUS UPDATE] updateMessageStatus reducer CALLED:`, {
        hasAction: !!action,
        hasPayload: !!action?.payload,
        payload: action?.payload,
        actionType: action?.type
      });
      
      if (!action || !action.payload) {
        console.error(`❌ [REDUX STATUS UPDATE] Invalid action or payload:`, { action });
        return;
      }
      
      const {
        contactId,
        messageId,
        tempId,
        status,
        timestamp,
        mediaThumbnailUrl: payloadMediaThumbnailUrl,
        mediaFileSizeBytes: payloadMediaFileSizeBytes,
      } = action.payload;

      // Normalize contactId for comparison (handle string/ObjectId differences)
      const normalizedContactId = contactId ? String(contactId).trim() : null;
      const stateContactId = state.messages?.contactID ? String(state.messages.contactID).trim() : null;

      // Normalize messageId for comparison (handle string/number/ObjectId differences)
      const normalizedMessageId = messageId ? String(messageId).trim() : null;
      const normalizedTempId = tempId ? String(tempId).trim() : null;
      
      // DEBUG: Log the normalized values for debugging
      console.log(`🔍 [REDUX STATUS UPDATE] Normalized values:`, {
        messageId: normalizedMessageId,
        tempId: normalizedTempId,
        status: status,
        originalMessageId: messageId,
        originalTempId: tempId
      });
      
      if (!normalizedMessageId) {
        console.warn(`⚠️ [REDUX STATUS UPDATE] No messageId provided for status update:`, {
          contactId,
          messageId,
          status,
          payload: action.payload
        });
        return;
      }

      // CRITICAL: Always cache the status update, even if contact isn't loaded
      // This ensures status updates aren't lost when they arrive before the contact is loaded
      statusUpdateCache.set(normalizedMessageId, {
        contactId: normalizedContactId,
        status,
        timestamp: timestamp || new Date().toISOString()
      });

      console.log(`🔄 [REDUX STATUS UPDATE] Processing updateMessageStatus:`, {
        normalizedContactId,
        stateContactId,
        contactIdMatch: normalizedContactId === stateContactId,
        messageId: normalizedMessageId,
        status,
        hasMessages: !!state.messages,
        chatsCount: state.messages?.chats?.length || 0,
        cached: true
      });

      // Try to apply the update if the contact is currently loaded
      if (normalizedContactId && stateContactId && normalizedContactId === stateContactId) {
        const searchId = normalizedMessageId;
        
        // Get sample of message IDs for debugging and check if message exists with different ID format
        const sampleIds = state.messages.chats.slice(0, 5).map(m => ({
          id: m.id ? String(m.id).trim() : null,
          tempId: m.tempId ? String(m.tempId).trim() : null,
          _id: m._id ? String(m._id).trim() : null
        }));
        
        // Also check all message IDs to see if the searchId exists in any form
        const allMessageIds = state.messages.chats.map(m => ({
          id: m.id ? String(m.id).trim() : null,
          tempId: m.tempId ? String(m.tempId).trim() : null,
          _id: m._id ? String(m._id).trim() : null,
          fullMessage: m
        }));
        
        // Check if searchId appears anywhere in the messages (for debugging)
        const foundInAnyForm = allMessageIds.find(m => 
          (m.id && m.id === searchId) || 
          (m.tempId && m.tempId === searchId) || 
          (m._id && m._id === searchId) ||
          (m.id && m.id.includes(searchId.substring(0, 20))) || // Partial match for debugging
          (searchId && m.id && m.id.includes(searchId.substring(0, 20)))
        );
        
        const messageIndex = state.messages.chats.findIndex((message, index) => {
          // Check all possible ID fields with string normalization and trimming
          const messageIdStr = message.id ? String(message.id).trim() : null;
          const messageTempIdStr = message.tempId ? String(message.tempId).trim() : null;
          const message_IdStr = message._id ? String(message._id).trim() : null;
          
          // CRITICAL: For Instagram messages, status updates come with Instagram message ID
          // but the message might still have tempId as its id. We need to check:
          // 1. Direct match: message.id === searchId OR message.tempId === searchId
          // 2. Cross-match: If status update has tempId, check if message.id === statusUpdate.tempId
          //    (This handles the case where message still has tempId as its id, but status update has Instagram ID)
          const directMatch = (
            (messageIdStr && messageIdStr === searchId) ||
            (messageTempIdStr && messageTempIdStr === searchId) ||
            (message_IdStr && message_IdStr === searchId)
          );
          
          // Cross-match: Match by tempId when status update has Instagram ID but message still has tempId
          // This handles: status update (id: Instagram ID, tempId: tempId) vs message (id: tempId, tempId: undefined or tempId)
          const crossMatch = normalizedTempId && (
            (messageIdStr && messageIdStr === normalizedTempId) ||
            (messageTempIdStr && messageTempIdStr === normalizedTempId)
          );
          
          const matches = directMatch || crossMatch;
          
          if (matches) {
            let matchedBy = 'unknown';
            if (messageIdStr === searchId) matchedBy = 'id-direct';
            else if (messageTempIdStr === searchId) matchedBy = 'tempId-direct';
            else if (message_IdStr === searchId) matchedBy = '_id-direct';
            else if (normalizedTempId && messageIdStr === normalizedTempId) matchedBy = 'tempId-cross-match-id';
            else if (normalizedTempId && messageTempIdStr === normalizedTempId) matchedBy = 'tempId-cross-match-tempId';
            
            console.log(`✅ [REDUX STATUS UPDATE] Found matching message at index ${index}:`, {
              messageId: message.id,
              tempId: message.tempId,
              _id: message._id,
              currentStatus: message.status,
              newStatus: status,
              searchId: searchId,
              statusUpdateTempId: normalizedTempId,
              matchedBy: matchedBy,
              directMatch: directMatch,
              crossMatch: crossMatch
            });
          }
          
          return matches;
        });
        
        // CRITICAL: If message not found by ID, check if it's a workflow message that needs ID update
        // Workflow messages start with tempId (workflow_...) and get updated to WhatsApp ID (wamid...)
        // The status update comes with WhatsApp ID, but Redux might still have tempId
        // Strategy: Look for workflow text messages that are still in 'sent' or 'delivered' status
        // and update the most recent one that matches the criteria
        if (messageIndex === -1 && searchId && searchId.startsWith('wamid.')) {
          // Find workflow text messages that might need ID update
          // Look for messages that:
          // 1. Are workflow messages (no senderid/clientid, or id/tempId starts with 'workflow_')
          // 2. Are text messages (type === 'text' or 'chat', messageType === 'text' or undefined)
          // 3. Are still in 'sent' or 'delivered' status (haven't been read yet)
          // 4. Have a workflow tempId as their ID (id starts with 'workflow_') - this is the key check!
          const now = Date.now();
          const workflowTextMessages = state.messages.chats
            .map((message, index) => ({ message, index }))
            .filter(({ message }) => {
              const tempId = message.tempId ? String(message.tempId).trim() : null;
              const messageId = message.id ? String(message.id).trim() : null;
              const isWorkflowMessage = !message.senderid && !message.clientid && !message.sendername && !message.clientname;
              const isWorkflowTempId = tempId && tempId.startsWith('workflow_');
              const hasWorkflowId = messageId && messageId.startsWith('workflow_');
              const hasWhatsAppId = messageId && messageId.startsWith('wamid.');
              const isPendingStatus = message.status === 'sent' || message.status === 'delivered';

              // Match workflow messages of ANY type (text, image, video, document, audio, etc.)
              // Previously this was gated on isTextMessage which silently dropped status updates
              // for all media sent by workflow automations.
              const hasWorkflowPrefix = hasWorkflowId || isWorkflowTempId;

              return isWorkflowMessage &&
                     hasWorkflowPrefix &&
                     !hasWhatsAppId &&
                     isPendingStatus;
            })
            .sort((a, b) => {
              // Sort by timestamp, most recent first
              const timeA = a.message.timestamp ? new Date(a.message.timestamp).getTime() : 0;
              const timeB = b.message.timestamp ? new Date(b.message.timestamp).getTime() : 0;
              return timeB - timeA;
            });
          
          // If we found workflow text messages, update the most recent one
          if (workflowTextMessages.length > 0) {
            const { message: workflowMessage, index: workflowMessageIndex } = workflowTextMessages[0];
            console.log(`🔄 [REDUX STATUS UPDATE] Found workflow text message, updating ID:`, {
              oldId: workflowMessage.id,
              oldTempId: workflowMessage.tempId,
              newId: searchId,
              messageIndex: workflowMessageIndex,
              type: workflowMessage.type,
              messageType: workflowMessage.messageType,
              status: workflowMessage.status,
              isWorkflow: !workflowMessage.senderid && !workflowMessage.clientid,
              timeSinceSent: workflowMessage.timestamp ? (now - new Date(workflowMessage.timestamp).getTime()) : 'unknown'
            });
            
            // Update the message ID to the WhatsApp ID and apply status update
            const existingMessage = workflowMessage;
            const currentStatus = existingMessage.status;
            const precedence = {
              seen: 4,
              read: 3,
              delivered: 2,
              sent: 1,
              pending: 0,
              failed: -1,
            };
            
            const currentPrecedence = precedence[currentStatus] ?? -1;
            const newPrecedence = precedence[status] ?? -1;
            
            // CRITICAL: Workflow messages are outgoing messages (no clientid/clientname)
            // Webhook status updates are authoritative and should correct incorrect "read" status
            const isCorrectingIncorrectReadStatus = 
              (currentStatus === "read" || currentStatus === "seen") &&
              (status === "delivered" || status === "sent" || status === "pending");
            
            const shouldUpdate = newPrecedence > currentPrecedence || 
                                status === currentStatus || 
                                isCorrectingIncorrectReadStatus;
            
            if (shouldUpdate) {
              state.messages.chats[workflowMessageIndex] = {
                ...existingMessage,
                id: searchId, // Update ID to WhatsApp ID
                tempId: existingMessage.tempId || existingMessage.id, // Keep tempId for reference (use id if tempId not set)
                status,
                // timestamp intentionally left as existingMessage.timestamp (via spread) —
                // it's a sort key; adopting the status webhook's time here would desync
                // it from send order and reshuffle the list on the next resort.
              };
              console.log(`✅ [REDUX STATUS UPDATE] Updated workflow message ID and status:`, {
                messageId: searchId,
                oldStatus: currentStatus,
                newStatus: status,
                oldId: existingMessage.id,
                tempId: existingMessage.tempId || existingMessage.id,
                corrected: isCorrectingIncorrectReadStatus
              });
              return; // Exit early since we've updated the message
            } else {
              console.log(`⏭️ [REDUX STATUS UPDATE] Workflow message found but status precedence check failed:`, {
                currentStatus,
                newStatus: status,
                currentPrecedence,
                newPrecedence
              });
            }
          } else {
            // Enhanced logging to help debug why workflow messages aren't found
            const allWorkflowMessages = state.messages.chats.filter(m => 
              !m.senderid && !m.clientid && (m.type === 'text' || m.type === 'chat')
            );
            
            if (isConsoleVerbose) {
            console.log(`⚠️ [REDUX STATUS UPDATE] No workflow text messages found to update:`, {
              searchId,
              totalChats: state.messages.chats.length,
              workflowMessagesCount: allWorkflowMessages.length,
              workflowMessages: allWorkflowMessages.map(m => ({
                id: m.id,
                tempId: m.tempId,
                type: m.type,
                messageType: m.messageType,
                status: m.status,
                hasWorkflowId: m.id && String(m.id).startsWith('workflow_'),
                hasWorkflowTempId: m.tempId && String(m.tempId).startsWith('workflow_'),
                hasWhatsAppId: m.id && String(m.id).startsWith('wamid.'),
                isPendingStatus: m.status === 'sent' || m.status === 'delivered',
                isTextMessage: (m.type === 'text' || m.type === 'chat') && (!m.messageType || m.messageType === 'text')
              })),
              note: 'Looking for workflow messages with workflow_ prefix in id or tempId, text type, and sent/delivered status'
            });
            }
            
            // CRITICAL FALLBACK: If no workflow message found by ID, try to find by matching tempId
            // Sometimes the status update comes with a wamid but we need to match it to a message with workflow_ tempId
            // This happens when the message was just sent and hasn't been updated in Redux yet
            if (allWorkflowMessages.length > 0) {
              // Try to find the most recent workflow text message that might match
              // Sort by timestamp (most recent first) and try to update the first one
              const recentWorkflowMessages = allWorkflowMessages
                .map((m, idx) => ({ message: m, originalIndex: state.messages.chats.findIndex(msg => msg === m) }))
                .filter(({ message }) => {
                  const tempId = message.tempId ? String(message.tempId).trim() : null;
                  const messageId = message.id ? String(message.id).trim() : null;
                  const hasWorkflowPrefix = (messageId && messageId.startsWith('workflow_')) || 
                                          (tempId && tempId.startsWith('workflow_'));
                  const hasWhatsAppId = messageId && messageId.startsWith('wamid.');
                  const isTextMessage = (message.type === 'text' || message.type === 'chat') && 
                                       (!message.messageType || message.messageType === 'text');
                  const isPendingStatus = message.status === 'sent' || message.status === 'delivered';
                  
                  return hasWorkflowPrefix && !hasWhatsAppId && isTextMessage && isPendingStatus;
                })
                .sort((a, b) => {
                  const timeA = a.message.timestamp ? new Date(a.message.timestamp).getTime() : 0;
                  const timeB = b.message.timestamp ? new Date(b.message.timestamp).getTime() : 0;
                  return timeB - timeA;
                });
              
              if (recentWorkflowMessages.length > 0) {
                const { message: fallbackMessage, originalIndex: fallbackIndex } = recentWorkflowMessages[0];
                console.log(`🔄 [REDUX STATUS UPDATE] FALLBACK: Found workflow message by tempId, updating ID:`, {
                  oldId: fallbackMessage.id,
                  oldTempId: fallbackMessage.tempId,
                  newId: searchId,
                  messageIndex: fallbackIndex,
                  type: fallbackMessage.type,
                  messageType: fallbackMessage.messageType,
                  status: fallbackMessage.status
                });
                
                const currentStatus = fallbackMessage.status;
                const precedence = {
                  seen: 4, read: 3, delivered: 2, sent: 1, pending: 0, failed: -1,
                };
                const currentPrecedence = precedence[currentStatus] ?? -1;
                const newPrecedence = precedence[status] ?? -1;
                const isCorrectingIncorrectReadStatus = 
                  (currentStatus === "read" || currentStatus === "seen") &&
                  (status === "delivered" || status === "sent" || status === "pending");
                const shouldUpdate = newPrecedence > currentPrecedence || 
                                    status === currentStatus || 
                                    isCorrectingIncorrectReadStatus;
                
                if (shouldUpdate) {
                  state.messages.chats[fallbackIndex] = {
                    ...fallbackMessage,
                    id: searchId, // Update ID to WhatsApp ID
                    tempId: fallbackMessage.tempId || fallbackMessage.id, // Keep tempId for reference
                    status,
                    // timestamp left as fallbackMessage.timestamp (via spread) — see note above.
                  };
                  console.log(`✅ [REDUX STATUS UPDATE] FALLBACK: Updated workflow message ID and status:`, {
                    messageId: searchId,
                    oldStatus: currentStatus,
                    newStatus: status,
                    oldId: fallbackMessage.id,
                    tempId: fallbackMessage.tempId || fallbackMessage.id
                  });
                  return; // Exit early since we've updated the message
                }
              }
            }
          }
        }

        // Instagram fallback: when status update (read/delivered) not matched by id/tempId, apply to most recent outgoing sent/delivered
        const isInstagramStatusUpdate = searchId && (
          searchId.length > 50 ||
          searchId.startsWith('aWdf') ||
          searchId.startsWith('temp-') ||
          searchId.startsWith('workflow_')
        );
        if (messageIndex === -1 && isInstagramStatusUpdate && (status === 'read' || status === 'delivered')) {
          const outgoingPending = state.messages.chats
            .map((message, index) => ({ message, index }))
            .filter(({ message }) => {
              const isOutgoing = !!message.senderid || (!message.clientid && !message.clientname);
              const isPendingStatus = message.status === 'sent' || message.status === 'delivered';
              return isOutgoing && isPendingStatus;
            })
            .sort((a, b) => {
              const timeA = a.message.timestamp ? new Date(a.message.timestamp).getTime() : 0;
              const timeB = b.message.timestamp ? new Date(b.message.timestamp).getTime() : 0;
              return timeB - timeA;
            });
          if (outgoingPending.length > 0) {
            const { message: instaMessage, index: instaIndex } = outgoingPending[0];
            const currentStatus = instaMessage.status;
            const precedence = { seen: 4, read: 3, delivered: 2, sent: 1, pending: 0, failed: -1 };
            const shouldUpdate = (precedence[status] ?? -1) >= (precedence[currentStatus] ?? -1);
            if (shouldUpdate) {
              state.messages.chats[instaIndex] = {
                ...instaMessage,
                id: searchId.length > 50 || searchId.startsWith('aWdf') ? searchId : instaMessage.id,
                tempId: instaMessage.tempId || instaMessage.id,
                status,
                // timestamp left as instaMessage.timestamp (via spread) — see note above.
              };
              console.log(`✅ [REDUX STATUS UPDATE] Instagram fallback: updated most recent outgoing:`, {
                oldId: instaMessage.id,
                newStatus: status,
                index: instaIndex,
              });
              return;
            }
          }
        }

        if (messageIndex !== -1) {
          const existingMessage = state.messages.chats[messageIndex];
          const currentStatus = existingMessage.status;
          
          // CRITICAL: Don't update status for internal mention messages
          // Internal mentions should always keep their 'internal' status
          if (currentStatus === "internal" || existingMessage.isInternalMention) {
            console.log(`🚫 [REDUX STATUS UPDATE] Skipping status update for internal mention message:`, {
              messageId,
              currentStatus,
              newStatus: status,
              isInternalMention: existingMessage.isInternalMention
            });
            return; // Exit early, don't update status
          }
          
          const precedence = {
            seen: 4,
            read: 3,
            delivered: 2,
            sent: 1,
            pending: 0,
            failed: -1,
          };

          // Update status if new status has higher precedence, or if status is explicitly provided
          const currentPrecedence = precedence[currentStatus] ?? -1;
          const newPrecedence = precedence[status] ?? -1;
          
          // CRITICAL: Check if this is an outgoing (team) message
          // Use senderid + !clientid only — WhatsApp may attach clientname on outbound rows; that must not flip this to false
          const isOutgoingMessage =
            !!(existingMessage.senderid || existingMessage.senderId) &&
            !existingMessage.clientid;
          
          // CRITICAL: For outgoing messages, webhook status updates are authoritative
          // BUT: We should NOT allow "sent" or "pending" to overwrite "read" or "seen" status
          // Only allow "delivered" to overwrite "read" if it's from a webhook (recent timestamp)
          // This prevents client-side "sent" status updates from downgrading correct "read" statuses
          const isCorrectingIncorrectReadStatus = isOutgoingMessage && 
            (currentStatus === "read" || currentStatus === "seen") &&
            (status === "delivered"); // Only allow "delivered" to correct "read", not "sent" or "pending"
          
          // A "failed" status is ranked below everything (-1), which silently dropped a live failure
          // — most visibly a WhatsApp template, which now sits on "pending" until Meta confirms it
          // and whose async failure (e.g. unpaid Meta account) arrives as a failed webhook. Let
          // "failed" replace "pending" ONLY: it never overrides sent/delivered/read, and nothing else
          // about the precedence order changes.
          const isFailedReplacingPending = status === "failed" && currentStatus === "pending";

          const shouldUpdate = newPrecedence > currentPrecedence ||
                              status === currentStatus ||
                              isCorrectingIncorrectReadStatus ||
                              isFailedReplacingPending;

          console.log(`📊 [REDUX STATUS UPDATE] Status precedence check:`, {
            currentStatus,
            newStatus: status,
            currentPrecedence,
            newPrecedence,
            isOutgoingMessage,
            isCorrectingIncorrectReadStatus,
            willUpdate: shouldUpdate
          });
          
          if (shouldUpdate) {
            // CRITICAL: For Instagram messages, if status update has Instagram message ID and message still has tempId as its id,
            // update the message ID to the Instagram message ID
            // This handles the transition from tempId → Instagram message ID
            const messageIdStr = existingMessage.id ? String(existingMessage.id).trim() : null;
            const messageTempIdStr = existingMessage.tempId ? String(existingMessage.tempId).trim() : null;
            const needsIdUpdate = (
              // Status update has Instagram message ID (not tempId)
              searchId && 
              searchId !== messageIdStr &&
              // Message was matched by tempId (meaning it still has tempId as its id or tempId matches)
              (normalizedTempId && (messageIdStr === normalizedTempId || messageTempIdStr === normalizedTempId)) &&
              // Status update ID looks like Instagram message ID (long base64-like string)
              (searchId.length > 50 || searchId.startsWith('aWdf'))
            );
            
            // Only update status, preserve all other fields
            state.messages.chats[messageIndex] = {
              ...existingMessage,
              // Update ID if needed (tempId → Instagram message ID transition)
              id: needsIdUpdate ? searchId : existingMessage.id,
              // Preserve tempId for reference
              tempId: existingMessage.tempId || (needsIdUpdate ? messageIdStr : null),
              status,
              // timestamp intentionally left as existingMessage.timestamp (via spread) —
              // it's a sort key (see addMessage's resort below); adopting the status
              // webhook's time here would desync it from send order and reshuffle the
              // list the next time any message triggers a resort.
              ...(payloadMediaThumbnailUrl
                ? { mediaThumbnailUrl: payloadMediaThumbnailUrl }
                : {}),
              ...(payloadMediaFileSizeBytes != null && Number.isFinite(Number(payloadMediaFileSizeBytes))
                ? { mediaFileSizeBytes: Number(payloadMediaFileSizeBytes) }
                : {}),
            };
            
            if (needsIdUpdate) {
              console.log(`🔄 [REDUX STATUS UPDATE] Updated message ID from tempId to Instagram message ID:`, {
                oldId: messageIdStr,
                newId: searchId,
                tempId: existingMessage.tempId,
                status: status
              });
            }
            console.log(`✅ [REDUX STATUS UPDATE] Status updated successfully:`, {
              messageId: existingMessage.id,
              tempId: existingMessage.tempId,
              _id: existingMessage._id,
              oldStatus: currentStatus,
              newStatus: status,
              corrected: isCorrectingIncorrectReadStatus,
              messageIndex,
              totalChats: state.messages.chats.length
            });
            
            // CRITICAL: Verify the update was applied by checking the state after mutation
            // This helps debug if Immer isn't working correctly
            const verifyUpdate = state.messages.chats[messageIndex];
            if (verifyUpdate && verifyUpdate.status === status) {
              console.log(`✅ [REDUX STATUS UPDATE] Verified: Status update applied correctly in state`);
            } else {
              console.error(`❌ [REDUX STATUS UPDATE] ERROR: Status update NOT applied! Expected: ${status}, Got: ${verifyUpdate?.status}`);
            }
          } else {
            console.log(`⏭️ [REDUX STATUS UPDATE] Status update skipped - lower precedence:`, {
              currentStatus,
              newStatus: status,
              currentPrecedence,
              newPrecedence,
              isOutgoingMessage,
              isCorrectingIncorrectReadStatus,
              messageId: existingMessage.id,
              tempId: existingMessage.tempId
            });
          }
        } else {
          if (isConsoleVerbose) {
          console.log(`⏭️ [REDUX STATUS UPDATE] Message not found in current chats, but cached for later:`, {
            searchId,
            contactId: normalizedContactId,
            totalChats: state.messages.chats.length,
            sampleIds,
            foundInAnyForm: foundInAnyForm ? {
              id: foundInAnyForm.id,
              tempId: foundInAnyForm.tempId,
              _id: foundInAnyForm._id,
              status: foundInAnyForm.fullMessage?.status
            } : null,
            allIdsSample: allMessageIds.slice(0, 10).map(m => m.id || m.tempId || m._id),
            note: 'Message may be in paginated section or not yet loaded. Status update cached and will be applied when message is loaded.'
          });
          }
          
          // CRITICAL: Try to apply cached updates to any messages that might match
          // This handles cases where the message exists but ID matching failed
          // Re-apply all cached updates for this contact to catch any missed updates
          if (state.messages && state.messages.chats && statusUpdateCache.size > 0) {
            const phoneNumberIDForCache = state.messages.wabaData?.phoneNumberID || null;
            const { chats: updatedChats, appliedCount } = applyCachedStatusUpdates(state.messages.chats, normalizedContactId, phoneNumberIDForCache);
            if (appliedCount > 0 && updatedChats !== state.messages.chats) {
              state.messages.chats = updatedChats;
              console.log(`✅ [REDUX STATUS UPDATE] Re-applied ${appliedCount} cached status updates after missing message`);
            }
          }
        }
      } else {
        console.log(`⏭️ [REDUX STATUS UPDATE] Contact not loaded, but status update cached:`, {
          normalizedContactId,
          stateContactId,
          messageId: normalizedMessageId,
          status,
          cacheSize: statusUpdateCache.size,
          note: 'Status update will be applied when contact is loaded'
        });
      }
      
      // CRITICAL: Log summary of what happened for debugging
      const finalCacheSize = statusUpdateCache.size;
      const wasCached = statusUpdateCache.has(normalizedMessageId);
      console.log(`📊 [REDUX STATUS UPDATE] Summary:`, {
        contactId: normalizedContactId,
        messageId: normalizedMessageId,
        status,
        wasContactLoaded: normalizedContactId === stateContactId,
        wasCached,
        cacheSize: finalCacheSize,
        contactChatsCount: state.messages?.chats?.length || 0
      });
    },
    updateMessageReaction(state, action) {
      const { contactId, messageId, reaction } = action.payload || {};
      if (!contactId || !messageId || !state.messages || !state.messages.chats) return;
      if (!inboxContactIdsMatch(state.messages.contactID, contactId)) return;
      const needle = String(messageId).trim();
      const idx = state.messages.chats.findIndex((c) => {
        const keys = [c?.id, c?._id, c?.tempId]
          .filter((k) => k != null && k !== "")
          .map((k) => String(k).trim());
        return keys.includes(needle);
      });
      if (idx === -1) return;
      state.messages.chats[idx].reaction = reaction ?? null;
    },
    updateMessageUrl(state, action) {
      const { contactId, messageId, url, mediaFileSizeBytes } = action.payload || {};
      if (!contactId || !messageId || !url) return;
      if (!state.messages || !state.messages.chats) return;
      if (!inboxContactIdsMatch(state.messages.contactID, contactId)) return;
      const needle = String(messageId).trim();
      const idx = state.messages.chats.findIndex((c) => {
        const keys = [c?.id, c?._id, c?.tempId]
          .filter((k) => k != null && k !== '')
          .map((k) => String(k).trim());
        return keys.includes(needle);
      });
      if (idx === -1) return;
      state.messages.chats[idx].url = url;
      state.messages.chats[idx].message = url;
      if (mediaFileSizeBytes) state.messages.chats[idx].mediaFileSizeBytes = mediaFileSizeBytes;
    },
    updateContactName(state, action) {
      const { contactId, name } = action.payload;
      if (state.messages && state.messages.contactID === contactId) {
        state.messages.contactName = name;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Messages
      .addCase(fetchMessagesByContact.pending, (state, action) => {
        const { contactId, skipCache } = action.meta.arg;
        // Background refreshes run silently behind already-shown cached data.
        // They must NOT touch loading or expectedContactId — doing so would:
        //   1. flash a loading spinner over the cached messages already in the UI, and
        //   2. clobber the expectedContactId set for a concurrent contact-switch,
        //      causing the new contact's fulfilled response to be rejected by the stale guard.
        if (skipCache) return;
        state.loading = true;
        state.error = null;
        state.expectedContactId = contactId;
      })
      .addCase(fetchMessagesByContact.fulfilled, (state, action) => {
        state.loading = false;
        const { data, contactId, isInitialLoad, isPagination } = action.payload;
        const metaArg = action.meta?.arg ?? {};
        const requestedJumpMessageId =
          metaArg.messageId != null && String(metaArg.messageId).trim() !== ""
            ? String(metaArg.messageId).trim()
            : null;
        /** Client explicitly requested a window around `messageId` (reply / jump-to). Not inferred from API body (response does not echo messageId). */
        const isJumpToMessageRequest = Boolean(requestedJumpMessageId);
        /** Treat as targeted load: jump request, or server returned a concrete index for the target. */
        const isTargetedLoad =
          isJumpToMessageRequest || typeof data?.targetMessagePosition === "number";
        
        // Get platform from data to determine if this is Instagram
        const platform = data?.platform || "whatsapp";

        console.log(`📥 [FETCH MESSAGES] fetchMessagesByContact.fulfilled called:`, {
          contactId,
          hasData: !!data,
          hasChats: !!(data && data.chats),
          chatsCount: data?.chats?.length || 0,
          isInitialLoad,
          isPagination,
          isJumpToMessageRequest,
          targetMessagePosition: data?.targetMessagePosition ?? null,
        });
        if (isJumpToMessageRequest) {
          console.log(`[REPLY-JUMP] fulfilled merge context`, {
            contactId,
            requestedJumpMessageIdPrefix: requestedJumpMessageId.slice(0, 48),
            isPaginationLoad: isPagination && !isInitialLoad,
            isTargetedLoad,
            willUseOlderPrependBranch:
              isPagination && !isInitialLoad && !isTargetedLoad,
          });
        }

        // Validate that this response is for the currently expected contact
        // This prevents race conditions where old requests complete after switching contacts
        const expectedStr =
          state.expectedContactId != null ? String(state.expectedContactId) : "";
        const receivedStr = contactId != null ? String(contactId) : "";
        if (expectedStr && receivedStr && expectedStr !== receivedStr) {
          console.log(`⏭️ [FETCH MESSAGES] Skipping - contact mismatch:`, {
            expected: state.expectedContactId,
            received: contactId
          });
          return;
        }

        // Late initial response after clearMessages() (tab switch / deselect): expected and messages were cleared — do not repopulate
        if (
          state.expectedContactId == null &&
          state.messages == null &&
          contactId &&
          !isPagination
        ) {
          return;
        }
        
        // Clear expected contact ID since we're processing the correct response
        state.expectedContactId = null;

        // Sort chats by timestamp before setting state
        if (data && data.chats) {
          // CRITICAL: Extract timestamps from RAW API data BEFORE any conversion
          // This ensures we preserve the original timestamp values from the database
          // Do NOT convert timestamps first - extract them directly from the raw data
          // WhatsApp-only sender identity. IG/FB must not use phoneNumberID.
          const businessSenderId =
            (platform === "whatsapp" || platform === "qr") ? (data.wabaData?.phoneNumberID || null) : null;
          
          // Track status corrections for logging
          let statusCorrectionsCount = 0;
          let cachedStatusUsedCount = 0;
          let outgoingMessagesWithReadStatus = 0;
          
          console.log(`🔄 [STATUS CORRECTION] Processing ${data.chats.length} messages for contact ${contactId}, cache size: ${statusUpdateCache.size}`);
          
          // DEBUG: Target message ID to track
          
          const normalizedChats = data.chats.map((chat, index) => {
            // CRITICAL: Preserve tempId if it exists - this is essential for status update matching
            // When messages are updated from tempId to Instagram message ID, tempId should still be preserved
            // This ensures status updates can match messages even after the ID transition
            const preservedTempId = chat.tempId || null;
            
            // CRITICAL: Fix incorrect "read" status for outgoing messages on refresh
            // Outgoing messages should only show "read" if they were actually read via WhatsApp webhook
            // The backend fix prevents new incorrect statuses, but old data might have incorrect statuses
            // We'll add a client-side correction to prevent showing "read" for outgoing messages that weren't actually read
            let correctedStatus = chat.status;
            
            // Outgoing/workflow-bulk detection: use the single shared implementation
            // (isOutgoingChatForReadPreserve, defined near the top of this file) instead
            // of re-deriving the same rules here — this file previously had this logic
            // duplicated in three places with subtly different senderid handling.
            const isOutgoingMessage = isOutgoingChatForReadPreserve(chat, businessSenderId);

            // Track outgoing messages with read status for debugging
            if (isOutgoingMessage && (chat.status === "read" || chat.status === "seen")) {
              outgoingMessagesWithReadStatus++;
            }
            
            // CRITICAL: Trust the database status - it's the source of truth
            // Don't use cache at all when loading from database - database is always correct
            // Cache will only be used for real-time updates via updateMessageStatus reducer
            // CRITICAL: For Instagram messages, ALWAYS trust the database status - don't reset to "sent"
            // Only apply status correction for WhatsApp messages
            if (platform === "instagram") {
              // For Instagram: trust database status completely, no correction needed
              // The database status is always correct for Instagram messages
              correctedStatus = chat.status; // Use database status as-is
            }
            // For WhatsApp: continue with existing correction logic below
            
            // Check all possible ID variations for logging/debugging
            const messageIdStr = chat.id ? String(chat.id).trim() : null;
            const messageTempIdStr = chat.tempId ? String(chat.tempId).trim() : null;
            const message_IdStr = chat._id ? String(chat._id).trim() : null;
            
            // DEBUG: Log status for specific message ID to verify database status
            const targetMessageId = "wamid.HBgMOTIzMjY3NDQ1NTc3FQIAERgSM0MzRkY3MkZBMUYxRUFDNUFFAA==";
            if (isConsoleVerbose && messageIdStr && (messageIdStr === targetMessageId || messageIdStr.includes("M0MzRkY3MkZBMUYxRUFDNUFF"))) {
              console.log(`🔍 [STATUS DEBUG] Target message found:`, {
                messageId: messageIdStr,
                dbStatus: chat.status,
                correctedStatus,
                wasModified: correctedStatus !== chat.status,
                isOutgoingMessage,
                businessSenderId,
                from: chat.from,
                senderid: chat.senderid
              });
            }
            
            // Raw timestamp fields already arrive as ISO strings (the backend runs
            // them through normalizeTimestampToISO before sending); this only guards
            // the rare Date/number/invalid shapes instead of repeating that branching
            // once per field, per message.
            const originalTime = toRawTimestampISOOrNull(chat.time);
            const originalTimestamp = toRawTimestampISOOrNull(chat.timestamp);
            const originalCreatedAt = toRawTimestampISOOrNull(chat.createdAt);
            // Assignment/closechat/blockcontact messages also carry a 'date' field, used for display in ChatBody.jsx
            const originalDate =
              (chat.type === "assignment" || chat.type === "closechat" || chat.type === "blockcontact")
                ? toRawTimestampISOOrNull(chat.date)
                : null;
            
            // CRITICAL: For each message, use its OWN timestamp values
            // Do NOT use a shared fallback that might be the same for all messages
            // Priority: time > timestamp > createdAt, but ONLY from this specific message
            // IMPORTANT: Always prefer the original timestamp values over any fallback
            const finalTime = originalTime || originalTimestamp || originalCreatedAt;
            const finalTimestamp = originalTimestamp || originalCreatedAt || originalTime;
            const finalCreatedAt = originalCreatedAt || originalTimestamp || originalTime;
            
            // CRITICAL: If no timestamp exists at all, generate a unique one based on message ID
            // This prevents all messages from getting the same timestamp
            // But ONLY if all timestamp fields are completely missing
            const hasAnyTimestamp = finalTime || finalTimestamp || finalCreatedAt;
            let finalTimeValue = finalTime;
            let finalTimestampValue = finalTimestamp;
            let finalCreatedAtValue = finalCreatedAt;
            
            if (!hasAnyTimestamp) {
              // Generate unique timestamp based on message ID to ensure uniqueness
              const uniqueOffset = index * 1000; // 1 second per message
              const baseTime = chat.id ? (parseInt(chat.id.toString().slice(-10), 36) || Date.now()) : Date.now();
              const fallbackTime = new Date(baseTime - uniqueOffset).toISOString();
              finalTimeValue = fallbackTime;
              finalTimestampValue = fallbackTime;
              finalCreatedAtValue = fallbackTime;
            }
            
            // CRITICAL: Now convert timestamps to local time for display, but preserve original values
            // Convert each timestamp individually to prevent any cross-contamination
            // Each message gets its own unique timestamp conversion
            const displayTime = finalTimeValue ? (convertToLocalTime(finalTimeValue)?.toISOString() || finalTimeValue) : finalTimeValue;
            const displayTimestamp = finalTimestampValue ? (convertToLocalTime(finalTimestampValue)?.toISOString() || finalTimestampValue) : finalTimestampValue;
            const displayCreatedAt = finalCreatedAtValue ? (convertToLocalTime(finalCreatedAtValue)?.toISOString() || finalCreatedAtValue) : finalCreatedAtValue;
            
            // CRITICAL: Check for template/list/catalog messages FIRST before URL detection
            // This prevents carousel template messages (which contain image URLs in JSON) from being misclassified as images
            const isTemplateFetch = chat.type === "template" || chat.messageType === "template";
            const isListFetch = chat.type === "list" || chat.messageType === "list";
            const isCatalogFetch = chat.type === "catalog" || chat.messageType === "catalog";
            
            // CRITICAL: Re-detect message type from body/URL to fix incorrect types from database
            // This ensures voice messages are correctly identified as audio, not video
            // BUT: Skip detection for template/list/catalog messages to preserve their type
            const messageBody = chat.message || chat.body || "";
            let detectedMessageType = null;
            if (!isTemplateFetch && !isListFetch && !isCatalogFetch && messageBody && typeof messageBody === 'string' && messageBody.trim()) {
              // CRITICAL: For Cloudinary URLs, check file extensions FIRST because Cloudinary
              // stores audio files with resource_type "video", so /video/upload/ appears in audio URLs too
              const isCloudinaryUrl = messageBody.match(/cloudinary\.com/i);
              
              if (isCloudinaryUrl) {
                // For Cloudinary URLs, check file extensions first (more reliable than path)
                if (messageBody.match(/\.(mp3|wav|ogg|m4a|aac|flac|oga|opus|amr|3gp)(\?|$)/i)) {
                  detectedMessageType = "audio";
                }
                else if (messageBody.match(/\.(mp4|webm|mov|avi|wmv|flv|mkv)(\?|$)/i)) {
                  detectedMessageType = "video";
                }
                else if (messageBody.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i)) {
                  detectedMessageType = "image";
                }
                // Then check paths (but audio path takes priority)
                else if (messageBody.match(/\/audio\//i)) {
                  detectedMessageType = "audio";
                }
                else if (messageBody.match(/\/video\//i)) {
                  detectedMessageType = "video";
                }
                else if (messageBody.match(/\/image\//i)) {
                  detectedMessageType = "image";
                }
              } else {
                // For non-Cloudinary URLs, check path-based patterns FIRST (more reliable than file extensions)
                // Check for audio path first to prevent voice messages from being detected as video
                if (messageBody.match(/\/audio\//i) ||
                    messageBody.match(/\/voice\//i) ||
                    messageBody.match(/\/sound\//i)) {
                  detectedMessageType = "audio";
                }
                // Check for video path
                else if (messageBody.match(/\/video\//i)) {
                  detectedMessageType = "video";
                }
                // Check for image path
                else if (messageBody.match(/\/image\//i)) {
                  detectedMessageType = "image";
                }
                // Then check file extensions (audio before video to prevent .ogg misclassification)
                else if (messageBody.match(/\.(mp3|wav|ogg|m4a|aac|flac|oga|opus|amr|3gp)(\?|$)/i)) {
                  detectedMessageType = "audio";
                }
                else if (messageBody.match(/\.(mp4|webm|mov|avi|wmv|flv|mkv)(\?|$)/i)) {
                  detectedMessageType = "video";
                }
                else if (messageBody.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i)) {
                  detectedMessageType = "image";
                }
              }
              
              // Detect document URLs (works for both Cloudinary and non-Cloudinary)
              if (!detectedMessageType && messageBody.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf)(\?|$)/i)) {
                detectedMessageType = "document";
              }
              if (!detectedMessageType && messageBody.match(/\/(document|documents)\//i) && messageBody.match(/\.(bin|pdf|doc|docx|xls|xlsx|ppt|pptx|txt)(\?|$)/i)) {
                detectedMessageType = "document";
              }
            }
            
            // CRITICAL: Check for catalog_purchase messages - they have JSON in the body
            // Don't override catalog_purchase type with detected types
            // Also detect by checking if body contains catalog purchase JSON structure
            let isCatalogPurchase = chat.type === "catalog_purchase" || chat.messageType === "catalog_purchase";
            if (!isCatalogPurchase && messageBody) {
              try {
                const parsedBody = typeof messageBody === 'string' ? JSON.parse(messageBody) : messageBody;
                // Check if it has the structure of a catalog purchase message
                if (parsedBody && typeof parsedBody === 'object' && 
                    (parsedBody.productId || parsedBody.catalogId || parsedBody.allProducts || parsedBody.items)) {
                  isCatalogPurchase = true;
                }
              } catch (e) {
                // Not JSON, ignore
              }
            }

            // WhatsApp location JSON in body (stored as text/type text if webhook path missed)
            let isLocationJson = chat.type === "location" || chat.messageType === "location";
            if (!isLocationJson && !isTemplateFetch && !isListFetch && !isCatalogFetch && !isCatalogPurchase && messageBody && typeof messageBody === 'string' && messageBody.trim().startsWith('{')) {
              try {
                const p = JSON.parse(messageBody);
                const lat = Number(p?.latitude);
                const lng = Number(p?.longitude);
                if (
                  p &&
                  typeof p === 'object' &&
                  !Number.isNaN(lat) &&
                  !Number.isNaN(lng) &&
                  !p.productId &&
                  !p.catalogId &&
                  !p.items &&
                  !p.allProducts &&
                  !p.components &&
                  !p.catalogType &&
                  !p.productData &&
                  !Array.isArray(p.rows)
                ) {
                  isLocationJson = true;
                }
              } catch (e) {
                /* ignore */
              }
            }

            // WhatsApp shared contact card(s): { contacts: [ { name, phones, ... } ] }
            let isContactsJson = chat.type === "contacts" || chat.messageType === "contacts";
            if (
              !isContactsJson &&
              !isLocationJson &&
              !isTemplateFetch &&
              !isListFetch &&
              !isCatalogFetch &&
              !isCatalogPurchase &&
              messageBody &&
              typeof messageBody === "string" &&
              messageBody.trim().startsWith("{")
            ) {
              try {
                const p = JSON.parse(messageBody);
                if (
                  p &&
                  typeof p === "object" &&
                  Array.isArray(p.contacts) &&
                  p.contacts.length > 0 &&
                  p.contacts[0] &&
                  typeof p.contacts[0] === "object" &&
                  (p.contacts[0].name || p.contacts[0].phones)
                ) {
                  isContactsJson = true;
                }
              } catch (e) {
                /* ignore */
              }
            }
            
            // CRITICAL: Always prioritize detected message type if available (it's more accurate)
            // FORCE override stored messageType if detection finds a different type
            // This fixes cases where database has "video" but detection finds "audio"
            // BUT: Don't override template/list/catalog/catalog_purchase messages
            let finalMessageType;
            if (isTemplateFetch) {
              // Preserve template type
              finalMessageType = "template";
            } else if (isListFetch) {
              // Preserve list type
              finalMessageType = "list";
            } else if (isCatalogFetch) {
              // Preserve catalog type
              finalMessageType = "catalog";
            } else if (isCatalogPurchase) {
              // Preserve catalog_purchase type
              finalMessageType = "catalog_purchase";
            } else if (isLocationJson) {
              finalMessageType = "location";
            } else if (isContactsJson) {
              finalMessageType = "contacts";
            } else if (detectedMessageType) {
              // Detection found a type - ALWAYS use it, even if stored type is different
              finalMessageType = detectedMessageType;
              // Debug log if stored type differs from detected type
              if (chat.messageType && chat.messageType !== detectedMessageType && 
                  (chat.messageType === "video" || chat.messageType === "audio")) {
                console.log(`🔧 [AUDIO FIX] Overriding stored type "${chat.messageType}" with detected type "${detectedMessageType}" for message:`, messageBody.substring(0, 100));
              }
            } else {
              // No detection - but check if stored type might be wrong
              // If stored type is "video" but message body suggests audio, try to correct it
              const storedType = chat.messageType || chat.type;
              if (storedType === "video" && messageBody) {
                // Check if this might actually be audio based on common audio patterns
                const isLikelyAudio = messageBody.match(/audio|voice|sound|\.(mp3|wav|ogg|m4a|aac|flac|oga)/i) && 
                                     !messageBody.match(/video|\.(mp4|webm|mov|avi|wmv|flv|mkv)/i);
                if (isLikelyAudio) {
                  finalMessageType = "audio";
                } else {
                  finalMessageType = (chat.messageType && chat.messageType !== "chat" ? chat.messageType : null) ||
                                    (chat.type && chat.type !== "chat" ? chat.type : "text");
                }
              } else {
                // No detection - use stored values
                finalMessageType = (chat.messageType && chat.messageType !== "chat" ? chat.messageType : null) ||
                                  (chat.type && chat.type !== "chat" ? chat.type : "text");
              }
            }
            
            // DEBUG: Log status for specific message ID if it matches
            if (isConsoleVerbose && messageIdStr && (messageIdStr === targetMessageId || messageIdStr.includes("M0MzRkY3MkZBMUYxRUFDNUFF"))) {
              console.log(`🔍 [STATUS DEBUG] Target message found:`, {
                messageId: messageIdStr,
                dbStatus: chat.status,
                correctedStatus,
                wasModified: correctedStatus !== chat.status,
                hasCache: !!(messageIdStr && statusUpdateCache.has(messageIdStr)) || 
                         !!(messageTempIdStr && statusUpdateCache.has(messageTempIdStr)) ||
                         !!(message_IdStr && statusUpdateCache.has(message_IdStr)),
                isOutgoingMessage,
                businessSenderId,
                from: chat.from,
                senderid: chat.senderid
              });
            }
            
            // For Facebook/Instagram media, prefer url so image/video/audio/document display correctly
            const isMediaTypeFetch = finalMessageType && ["image", "video", "audio", "document"].includes(finalMessageType);
            const messageForDisplay = isMediaTypeFetch && chat.url ? chat.url : (chat.message || chat.body || "");

            return {
            ...chat,
            // CRITICAL: Preserve tempId if it exists - essential for status update matching
            ...(preservedTempId && { tempId: preservedTempId }),
            // CRITICAL: Use corrected status for outgoing messages
            status: correctedStatus,
            // Use detected type if available, otherwise fall back to stored values
            messageType: finalMessageType,
            message: messageForDisplay,
            // Mark outgoing messages so selectUnreadIncomingMessages can filter them correctly.
            // Without this, !chat.isFromMe === !undefined === true for every API-loaded message,
            // causing all delivered outgoing messages to appear as unread.
            isFromMe: isOutgoingMessage,
            ...(chat.url && { url: chat.url }),
            ...(chat.mediaThumbnailUrl && { mediaThumbnailUrl: chat.mediaThumbnailUrl }),
            ...(chat.mediaFileSizeBytes != null && Number.isFinite(Number(chat.mediaFileSizeBytes))
              ? { mediaFileSizeBytes: Number(chat.mediaFileSizeBytes) }
              : {}),
            ...((chat.documentFileName || chat.filename) && String(chat.documentFileName || chat.filename).trim()
              ? { documentFileName: String(chat.documentFileName || chat.filename).trim() }
              : {}),
            // ChatBody checks for type === "chat" to render messages; CRITICAL: always preserve assignment/closechat/blockcontact so they show after reload (Instagram)
            type: (chat.type === "assignment" || chat.type === "closechat" || chat.type === "blockcontact")
              ? chat.type
              : ((finalMessageType && ["image", "video", "audio", "document", "location", "contacts", "text", "list", "catalog", "catalog_purchase"].includes(finalMessageType))
                  ? "chat"
                  : (chat.type || "chat")),
              // CRITICAL: Preserve individual message timestamps - use the specific timestamp for this message
              // Ensure time field uses the message's own timestamp, not a fallback that might be shared
              // IMPORTANT: Always preserve the original timestamp if it exists, even if it seems wrong
              // Use converted timestamps for display, but they're based on the original unique values
              time: displayTime,
              // Ensure timestamp is also preserved for sorting - use this message's own timestamp
              timestamp: displayTimestamp,
              // Ensure createdAt is preserved for proper ordering - use this message's own createdAt
              createdAt: displayCreatedAt,
            // CRITICAL: For assignment messages, preserve toName and to for ChatBody.jsx display
            // Also preserve the date field with the correct timestamp
            ...(chat.type === "assignment" && {
              to: chat.to !== undefined ? chat.to : null,
              toName: chat.toName !== undefined ? chat.toName : null,
              // CRITICAL: Use the extracted originalDate if available, otherwise use the message's own timestamp
              // This ensures each assignment message gets its unique timestamp
              date: originalDate || displayTimestamp || displayTime || displayCreatedAt,
            }),
            // CRITICAL: For closechat messages, preserve date field with correct timestamp
            ...(chat.type === "closechat" && {
              // CRITICAL: Use the extracted originalDate if available, otherwise use the message's own timestamp
              // This ensures each closechat message gets its unique timestamp
              date: originalDate || displayTimestamp || displayTime || displayCreatedAt,
            }),
            // CRITICAL: For blockcontact messages, preserve date field with correct timestamp
            ...(chat.type === "blockcontact" && {
              date: originalDate || displayTimestamp || displayTime || displayCreatedAt,
            }),
            };
          });
          
          // Log status correction summary - always log to see what's happening
          if (isConsoleVerbose) {
          const statusBreakdown = {};
          normalizedChats.forEach(chat => {
            const status = chat.status || 'unknown';
            statusBreakdown[status] = (statusBreakdown[status] || 0) + 1;
          });
          console.log(`📊 [STATUS CORRECTION SUMMARY] Contact ${contactId}:`, {
            totalMessages: data.chats.length,
            outgoingMessagesWithReadStatus,
            statusCorrections: statusCorrectionsCount,
            cachedStatusUsed: cachedStatusUsedCount,
            cacheSize: statusUpdateCache.size,
            businessSenderId,
            statusBreakdown,
            outgoingMessages: normalizedChats.filter(c => !!c.senderid && !c.clientid).length
          });
          }
          
          // DEBUG: Log normalized timestamps
          // Normalized chats ready for display
          
          const sortedChats = normalizedChats.slice().sort((a, b) => {
            const timeA = new Date(a.timestamp).getTime();
            const timeB = new Date(b.timestamp).getTime();
            
            // Primary sort: by timestamp
            if (timeA !== timeB) {
              return timeA - timeB; // Ascending order (oldest first)
            }
            
            // Secondary sort: by createdAt for identical timestamps
            // This ensures messages sent in quick succession maintain proper insertion order
            if (a.createdAt && b.createdAt) {
              return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            }
            
            // Fallback: use message ID if createdAt is not available
            return (a.id || '').localeCompare(b.id || '');
          });
          
          // Handle pagination: merge or replace messages based on load type
          let mergedChats = sortedChats;
          const isPaginationLoad = isPagination && !isInitialLoad;
          
          if (
            state.messages &&
            state.messages.chats &&
            inboxContactIdsMatch(state.messages.contactID, contactId)
          ) {
            if (isPaginationLoad && !isTargetedLoad) {
              // Loading older messages: prepend new messages to existing ones
              // Remove duplicates based on message ID
              const existingIds = new Set(state.messages.chats.map(chat => chat.id || chat._id || chat.tempId));
              const newChats = sortedChats.filter(chat => !existingIds.has(chat.id || chat._id || chat.tempId));
              
              // Only create new array if there are actually new chats
              if (newChats.length > 0) {
                mergedChats = [...newChats, ...state.messages.chats];
              } else {
                // No new chats, reuse existing array to prevent unnecessary re-renders
                mergedChats = state.messages.chats;
              }
            } else {
              // Initial load or targeted load: merge socket messages with API messages
              // CRITICAL: Always preserve socket messages that arrived before/during the API fetch
              const existingChats = state.messages.chats;
              const apiChatIds = new Set(sortedChats.map(chat => chat.id || chat._id || chat.tempId));
              
              // Add socket messages that aren't in the API response (including the latest message)
              // Own sends the server has confirmed AND already returned under their real id are not "socket only" -- see
              // findSupersededOptimisticChatIds (the thread rows carry no tempId, so the saved temp- bubble would otherwise show twice).
              const supersededOptimisticIds = findSupersededOptimisticChatIds(existingChats, sortedChats);
              const socketOnlyChats = existingChats.filter(chat =>
                !apiChatIds.has(chat.id || chat._id || chat.tempId) &&
                !supersededOptimisticIds.has(String(chat.id || chat._id || chat.tempId))
              );
              
              // Only merge if there are socket-only chats or if the content actually changed
              const existingChatIds = new Set(existingChats.map(chat => chat.id || chat._id || chat.tempId));
              const hasNewChats = sortedChats.some(chat => !existingChatIds.has(chat.id || chat._id || chat.tempId));
              
              if (socketOnlyChats.length > 0 || hasNewChats || isJumpToMessageRequest) {
                // Merge and sort: API messages + socket-only messages
                // This ensures the latest message from socket is included even if not in API response
                // CRITICAL: Preserve original timestamps from both API and socket messages
                // Do NOT overwrite timestamps during merge
                mergedChats = [...sortedChats, ...socketOnlyChats].sort((a, b) => {
                  // CRITICAL: Use each message's own timestamp - don't use a shared fallback
                  // Extract timestamps individually to prevent any cross-contamination
                  const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 
                               (a.createdAt ? new Date(a.createdAt).getTime() : 
                               (a.time ? new Date(a.time).getTime() : 0));
                  const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 
                               (b.createdAt ? new Date(b.createdAt).getTime() : 
                               (b.time ? new Date(b.time).getTime() : 0));
                  
                  // Primary sort: by timestamp
                  if (timeA !== timeB) {
                    return timeA - timeB;
                  }
                  
                  // Secondary sort: by createdAt for identical timestamps
                  if (a.createdAt && b.createdAt) {
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                  }
                  
                  // Fallback: use message ID if createdAt is not available
                  return (a.id || '').localeCompare(b.id || '');
                });
              } else {
                // Content hasn't changed, reuse existing array to prevent unnecessary re-renders
                mergedChats = existingChats;
              }
            }
          }

          const priorChatsForReadPreserve =
            inboxContactIdsMatch(state.messages?.contactID, contactId) &&
            Array.isArray(state.messages.chats)
              ? state.messages.chats
              : [];
          const phoneForReadPreserve =
            data?.wabaData?.phoneNumberID || state.messages?.wabaData?.phoneNumberID || null;
          mergedChats = applyStaleFetchIncomingReadPreserve(
            mergedChats,
            priorChatsForReadPreserve,
            phoneForReadPreserve
          );
          // Same idea for OUR OWN sends, in the other direction: bring stale on-screen statuses up to the server's, never downgrade a
          // newer one (see applyFurtherOutgoingStatuses). Not for the "load older messages" pagination path, which is left untouched.
          if (!isPaginationLoad || isTargetedLoad) {
            mergedChats = applyFurtherOutgoingStatuses(
              mergedChats,
              priorChatsForReadPreserve,
              sortedChats,
              phoneForReadPreserve
            );
          }
          
          // Only update state if content actually changed to prevent infinite re-renders
          const currentChats = state.messages?.chats || [];
          const chatsChanged = 
            mergedChats.length !== currentChats.length ||
            mergedChats.some((newChat, index) => {
              const currentChat = currentChats[index];
              return !currentChat || 
                     (newChat.id || newChat._id || newChat.tempId) !== (currentChat.id || currentChat._id || currentChat.tempId) ||
                     newChat.message !== currentChat.message ||
                     newChat.status !== currentChat.status ||
                     newChat.time !== currentChat.time;
            });
          
          // CRITICAL: Clear cache entries for messages loaded from database
          // Database is the source of truth, so we should clear stale cache entries
          // Don't apply cached updates when loading from DB - only use DB status
          mergedChats.forEach((chat) => {
            const messageIdStr = chat.id ? String(chat.id).trim() : null;
            const messageTempIdStr = chat.tempId ? String(chat.tempId).trim() : null;
            const message_IdStr = chat._id ? String(chat._id).trim() : null;
            
            // Clear cache for all ID variations since we have fresh data from database
            if (messageIdStr && statusUpdateCache.has(messageIdStr)) {
              statusUpdateCache.delete(messageIdStr);
            }
            if (messageTempIdStr && statusUpdateCache.has(messageTempIdStr)) {
              statusUpdateCache.delete(messageTempIdStr);
            }
            if (message_IdStr && statusUpdateCache.has(message_IdStr)) {
              statusUpdateCache.delete(message_IdStr);
            }
          });
          
          // CRITICAL: Use database status directly - don't apply cached updates on load
          // Database is the source of truth. Cache will only be used for real-time updates
          // that arrive AFTER this database load
          if (
            chatsChanged ||
            !state.messages ||
            !inboxContactIdsMatch(state.messages.contactID, contactId) ||
            isJumpToMessageRequest
          ) {
            // Set all messages at once with merged chats and pagination metadata
            state.messages = {
              ...data,
              totalChats: data.totalChats || mergedChats.length,
              loadedChats: mergedChats.length, // cumulative count of all messages loaded in client
              hasMore: data.hasMore !== undefined ? data.hasMore : (mergedChats.length < (data.totalChats || 0)),
              offset: data.offset !== undefined ? data.offset : 0,
              targetMessagePosition:
                typeof data.targetMessagePosition === "number"
                  ? data.targetMessagePosition
                  : data.targetMessagePosition ?? null,
              chats: mergedChats
            };
            if (isJumpToMessageRequest) {
              console.log(`[REPLY-JUMP] state.messages applied`, {
                contactId,
                chatsLen: mergedChats.length,
                offset: state.messages.offset,
                targetMessagePosition: state.messages.targetMessagePosition,
                hasMore: state.messages.hasMore,
              });
            }
          }
        }
        // Note: the controller always returns `chats` as an array (even `[]` for an
        // empty conversation), so `data.chats` is truthy on every fulfilled response —
        // there is no code path where this reducer runs without chats.
      })
      .addCase(fetchMessagesByContact.rejected, (state, action) => {
        // Duplicate fetch debounced while the first request is still in flight — do not clear
        // expectedContactId or loading; otherwise fulfilled drops data (stale guard) and the UI sticks on "Loading conversation..."
        if (action.payload === "Debounced") {
          state.error = null;
          return;
        }
        state.loading = false;
        state.expectedContactId = null;
        let errorMessage = "Something went wrong";
        if (action.payload) {
          if (typeof action.payload === "string") {
            errorMessage = action.payload;
          } else if (typeof action.payload === "object" && action.payload.message) {
            errorMessage = action.payload.message;
          }
        }
        state.error = errorMessage;
      })
      // fetchMessagesByGroup -- group analogue of fetchMessagesByContact's own pending/fulfilled/
      // rejected trio just above, deliberately without its status-correction/cache-merge logic
      // (see fetchMessagesByGroup's own doc comment for why that's not needed here). Writes into
      // the exact same state.messages/state.loading/state.expectedContactId fields.
      .addCase(fetchMessagesByGroup.pending, (state, action) => {
        const { groupId } = action.meta.arg;
        state.loading = true;
        state.error = null;
        state.expectedContactId = groupId;
      })
      .addCase(fetchMessagesByGroup.fulfilled, (state, action) => {
        state.loading = false;
        const { data, groupId } = action.payload;

        const expectedStr = state.expectedContactId != null ? String(state.expectedContactId) : "";
        const receivedStr = groupId != null ? String(groupId) : "";
        if (expectedStr && receivedStr && expectedStr !== receivedStr) {
          return; // superseded by a newer selection, same guard fetchMessagesByContact uses
        }
        state.expectedContactId = null;

        if (data && data.chats) {
          const normalizedChats = data.chats.map((chat) => ({
            ...chat,
            timestamp: chat.timestamp ? new Date(chat.timestamp) : new Date(),
            time: chat.time ? new Date(chat.time) : (chat.timestamp ? new Date(chat.timestamp) : new Date()),
            createdAt: chat.createdAt ? new Date(chat.createdAt) : (chat.timestamp ? new Date(chat.timestamp) : new Date()),
          }));
          state.messages = { ...data, chats: normalizedChats };
        } else {
          state.messages = data || null;
        }
      })
      .addCase(fetchMessagesByGroup.rejected, (state, action) => {
        state.loading = false;
        state.expectedContactId = null;
        state.error = typeof action.payload === "string" ? action.payload : "Failed to load group messages";
      })
      .addCase(fetchOlderGroupMessages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOlderGroupMessages.fulfilled, (state) => {
        // Handled by fetchMessagesByGroup.fulfilled, same as fetchOlderMessages above.
        state.loading = false;
      })
      .addCase(fetchOlderGroupMessages.rejected, (state, action) => {
        state.loading = false;
        console.error("❌ Error fetching older group messages:", action.payload);
      })
      // refreshMessagesBackground dispatches fetchMessagesByContact(skipCache:true) which updates Redux via its own fulfilled case.
      .addCase(refreshMessagesBackground.fulfilled, () => { /* Redux update handled by fetchMessagesByContact.fulfilled */ })
      .addCase(refreshMessagesBackground.pending, () => { /* no loading spinner for background refresh */ })
      .addCase(refreshMessagesBackground.rejected, () => { /* silent — stale data still visible */ })
      // Handle fetchOlderMessages
      .addCase(fetchOlderMessages.pending, (state) => {
        // Keep loading state for pagination
        state.loading = true;
      })
      .addCase(fetchOlderMessages.fulfilled, (state) => {
        // This will be handled by fetchMessagesByContact.fulfilled
        state.loading = false;
      })
      .addCase(fetchOlderMessages.rejected, (state, action) => {
        state.loading = false;
        console.error("❌ Error fetching older messages:", action.payload);
      })
      // Handle fetchMessagesAroundMessage
      .addCase(fetchMessagesAroundMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMessagesAroundMessage.fulfilled, (state) => {
        // This will be handled by fetchMessagesByContact.fulfilled
        state.loading = false;
      })
      .addCase(fetchMessagesAroundMessage.rejected, (state, action) => {
        state.loading = false;
        console.error("❌ Error fetching messages around target:", action.payload);
      })
      // Send Message
      .addCase(sendMessage.pending, (state, action) => {
        state.messageSendingLoading = true;
        state.error = null;

        // Don't add pending message here since it's already added in TextBox component
        // This prevents duplicate pending messages
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.messageSendingLoading = false;
        state.error = null;

        if (action.payload.success && action.payload.data) {
          const { tempId, chats } = action.payload.data;
          if (chats && chats.length > 0) {
            const sentContactId = action.meta.arg?.contactId;
            if (sentContactId && state.messages?.contactID &&
                String(state.messages.contactID) !== String(sentContactId)) {
              return;
            }
            if (!state.messages?.chats) return;

            // Convert timestamps to local time for the API response
            const localizedChat = convertMessagesTimestamps([chats[0]])[0];

            const messageIndex = state.messages.chats.findIndex(
              (chat) => chat.tempId === tempId || chat.id === tempId
            );

            // Current user info now comes from the thunk (computed once from localStorage
            // there) instead of re-reading/re-parsing localStorage on every reducer run.
            const parsedUser = action.payload.currentUser || null;

            if (messageIndex !== -1) {
              // This is a message we sent, so preserve our sender info. The array is
              // already sorted and this entry's position/timestamp is unchanged
              // (it's the same optimistic message being confirmed), so no re-sort needed.
              state.messages.chats[messageIndex] = {
                ...localizedChat,
                tempId,
                sendername: parsedUser?.name || "You",
                senderid: parsedUser?._id,
                isFromMe: true,
              };
            } else {
              // Check if this is our message
              const isFromCurrentUser = localizedChat.senderid === parsedUser?._id;
              const newChat = isFromCurrentUser
                ? {
                    ...localizedChat,
                    tempId,
                    sendername: parsedUser?.name || "You",
                    senderid: parsedUser?._id,
                    isFromMe: true,
                  }
                : localizedChat;

              // The array is already sorted ascending by timestamp — insert at the
              // correct position instead of re-sorting the whole list on every send.
              // A freshly-sent message's timestamp is "now", so this is almost always
              // an O(1) append; the backward scan only walks further when out-of-order
              // arrivals require it.
              const newTime = new Date(newChat.timestamp).getTime();
              let insertAt = state.messages.chats.length;
              while (insertAt > 0 && new Date(state.messages.chats[insertAt - 1].timestamp).getTime() > newTime) {
                insertAt--;
              }
              state.messages.chats.splice(insertAt, 0, newChat);
            }
          }
        }

        // IDB snapshot is now stale — sent message exists in Redux but not in the cached
        // API response. Invalidate so the next contact switch + return triggers a fresh fetch.
        const sentContactId = action.meta.arg?.contactId;
        if (sentContactId) {
          invalidate(STORES.MESSAGES, String(sentContactId)); // fire-and-forget
          messageCache.delete(String(sentContactId));
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.messageSendingLoading = false;
        
        // Don't set error for duplicate send prevention
        if (action.payload !== "Duplicate send prevented") {
          // Safely extract error message
          let errorMessage = "Failed to send message";
          if (action.payload) {
            if (typeof action.payload === 'string') {
              errorMessage = action.payload;
            } else if (typeof action.payload === 'object' && action.payload.message) {
              errorMessage = action.payload.message;
            }
          }
          if (!takeQrRefusal(action.meta.arg?.tempId)) state.error = errorMessage;
        }

        const { tempId } = action.meta.arg;
        
        // Don't mark as failed if it was just duplicate prevention
        if (action.payload !== "Duplicate send prevented") {
          const messageIndex = state.messages.chats.findIndex(
            (chat) => chat.tempId === tempId || chat.id === tempId
          );

          if (messageIndex !== -1) {
            state.messages.chats[messageIndex].status = "failed";
          }
        }
      })

      // Send Image by Upload
      .addCase(sendImageByUpload.pending, (state, action) => {
        state.messageSendingLoading = true;
        state.error = null;

        // Don't add pending message here since it's already added in TextBox component
        // This prevents duplicate pending messages
      })
      .addCase(sendImageByUpload.fulfilled, (state, action) => {
        state.messageSendingLoading = false;

        if (action.payload.success) {
          const sentContactId = action.meta.arg?.contactId;
          if (sentContactId && state.messages?.contactID &&
              String(state.messages.contactID) !== String(sentContactId)) {
            return;
          }
          if (!state.messages?.chats) return;

          const newImageMessage = action.payload.data?.chats?.[0];
          const tempId = action.payload.data?.tempId;

          // If the server didn't return a chat object, just mark the pending message as sent
          // so it doesn't get stuck or disappear.
          if (!newImageMessage) {
            const fallbackIndex = state.messages.chats.findIndex(
              (chat) => chat.tempId === tempId || chat.id === tempId
            );
            if (fallbackIndex !== -1) {
              state.messages.chats[fallbackIndex] = {
                ...state.messages.chats[fallbackIndex],
                status: "sent",
              };
            }
            return;
          }

          // Convert timestamps to local time for the API response
          const localizedImageMessage = convertMessagesTimestamps([newImageMessage])[0];

          // Find the message by tempId
          const messageIndex = state.messages.chats.findIndex(
            (chat) => chat.tempId === tempId || chat.id === tempId
          );

          if (messageIndex !== -1) {
            const existing = state.messages.chats[messageIndex];
            state.messages.chats[messageIndex] = {
              ...existing,
              ...localizedImageMessage,
              // Always keep the pending message's own timestamp/createdAt (click-time order) —
              // never adopt the server's, which is assigned when the upload finishes and can
              // desync from true send order across a fast burst of sends.
              timestamp: existing.timestamp,
              createdAt: existing.createdAt,
              tempId: tempId,
              status: "sent",
            };
          } else {
            // If not found, add as new
            state.messages.chats = [...state.messages.chats, {
              ...localizedImageMessage,
              tempId: tempId,
              status: "sent",
            }];

            // Sort chats by creating new array with secondary sort key
            state.messages.chats = state.messages.chats.slice().sort((a, b) => {
              const timeA = new Date(a.timestamp).getTime();
              const timeB = new Date(b.timestamp).getTime();
              
              // Primary sort: by timestamp
              if (timeA !== timeB) {
                return timeA - timeB; // Ascending order (oldest first)
              }
              
              // Secondary sort: by createdAt for identical timestamps
              // This ensures messages sent in quick succession maintain proper insertion order
              if (a.createdAt && b.createdAt) {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
              }
              
              // Fallback: use message ID if createdAt is not available
              return (a.id || '').localeCompare(b.id || '');
            });
          }
          invalidate(STORES.MESSAGES, String(sentContactId)); // fire-and-forget
          messageCache.delete(String(sentContactId));
        }
      })
      // Send gallery item
      .addCase(sendGalleryItemMessage.pending, (state, action) => {
        state.messageSendingLoading = true;
        state.error = null;

        // Don't add pending message here since it's already added in TextBox component
        // This prevents duplicate pending messages
      })
      .addCase(sendGalleryItemMessage.fulfilled, (state, action) => {
        state.messageSendingLoading = false;

        if (action.payload.success) {
          const sentContactId = action.meta.arg?.contactId;
          if (sentContactId && state.messages?.contactID &&
              String(state.messages.contactID) !== String(sentContactId)) {
            return;
          }
          if (!state.messages?.chats) return;

          const newGalleryMessage = action.payload.data?.chats?.[0];
          const tempId = action.payload.data?.tempId ?? action.meta?.arg?.tempId;

          // If the server didn't return a chat object (Instagram/Facebook success
          // path only returns messageId — socket updates the row), just mark the
          // pending message as sent so it doesn't get stuck or disappear.
          if (!newGalleryMessage) {
            const fallbackIndex = state.messages.chats.findIndex(
              (chat) => chat.tempId === tempId || chat.id === tempId
            );
            if (fallbackIndex !== -1) {
              state.messages.chats[fallbackIndex] = {
                ...state.messages.chats[fallbackIndex],
                status: "sent",
              };
            }
            return;
          }

          // Convert timestamps to local time for the API response
          const localizedGalleryMessage = convertMessagesTimestamps([newGalleryMessage])[0];

          // Find the message by tempId
          const messageIndex = state.messages.chats.findIndex(
            (chat) => chat.tempId === tempId || chat.id === tempId
          );

          if (messageIndex !== -1) {
            const existing = state.messages.chats[messageIndex];
            state.messages.chats[messageIndex] = {
              ...existing,
              ...localizedGalleryMessage,
              // Always keep the pending message's own timestamp/createdAt (click-time order) —
              // never adopt the server's, which is assigned when the upload finishes and can
              // desync from true send order across a fast burst of sends.
              timestamp: existing.timestamp,
              createdAt: existing.createdAt,
              tempId: tempId,
              status: "sent",
            };
          } else {
            // If not found, add as new
            state.messages.chats = [...state.messages.chats, {
              ...localizedGalleryMessage,
              tempId: tempId,
              status: "sent",
            }];

            // Sort chats by creating new array with secondary sort key
            state.messages.chats = state.messages.chats.slice().sort((a, b) => {
              const timeA = new Date(a.timestamp).getTime();
              const timeB = new Date(b.timestamp).getTime();

              // Primary sort: by timestamp
              if (timeA !== timeB) {
                return timeA - timeB; // Ascending order (oldest first)
              }

              // Secondary sort: by createdAt for identical timestamps
              // This ensures messages sent in quick succession maintain proper insertion order
              if (a.createdAt && b.createdAt) {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
              }

              // Fallback: use message ID if createdAt is not available
              return (a.id || '').localeCompare(b.id || '');
            });
          }
          invalidate(STORES.MESSAGES, String(sentContactId)); // fire-and-forget
          messageCache.delete(String(sentContactId));
        }
      })
      .addCase(sendGalleryItemMessage.rejected, (state, action) => {
        state.messageSendingLoading = false;
        // Safely extract error message
        let errorMessage = "Failed to send the gallery item";
        if (action.payload) {
          if (typeof action.payload === 'string') {
            errorMessage = action.payload;
          } else if (typeof action.payload === 'object' && action.payload.message) {
            errorMessage = action.payload.message;
          }
        }
        state.error = errorMessage;

        const { tempId } = action.meta.arg;

        // Find and update the message status
        const messageIndex = state.messages.chats.findIndex(
          (chat) => chat.tempId === tempId || chat.id === tempId
        );

        if (messageIndex !== -1) {
          state.messages.chats[messageIndex].status = "error";
        }
      })
      .addCase(sendImageByUpload.rejected, (state, action) => {
        state.messageSendingLoading = false;
        // Safely extract error message
        let errorMessage = "Failed to send the image message";
        if (action.payload) {
          if (typeof action.payload === 'string') {
            errorMessage = action.payload;
          } else if (typeof action.payload === 'object' && action.payload.message) {
            errorMessage = action.payload.message;
          }
        }
        if (!takeQrRefusal(action.meta.arg?.tempId)) state.error = errorMessage;

        const { tempId } = action.meta.arg;

        // Find and update the message status
        const messageIndex = state.messages.chats.findIndex(
          (chat) => chat.tempId === tempId || chat.id === tempId
        );

        if (messageIndex !== -1) {
          state.messages.chats[messageIndex].status = "error";
        }
      })

      // Send Video by Upload
      .addCase(sendVideoByUpload.pending, (state, action) => {
        state.messageSendingLoading = true;
        state.error = null;

        // Don't add pending message here since it's already added in TextBox component
        // This prevents duplicate pending messages
      })
      .addCase(sendVideoByUpload.fulfilled, (state, action) => {
        state.messageSendingLoading = false;

        if (action.payload.success) {
          const sentContactId = action.meta.arg?.contactId;
          if (sentContactId && state.messages?.contactID &&
              String(state.messages.contactID) !== String(sentContactId)) {
            return;
          }
          if (!state.messages?.chats) return;

          const newVideoMessage = action.payload.data?.chats?.[0];
          const tempId = action.payload.data?.tempId;

          if (!newVideoMessage) {
            const fallbackIndex = state.messages.chats.findIndex(
              (chat) => chat.tempId === tempId || chat.id === tempId
            );
            if (fallbackIndex !== -1) {
              state.messages.chats[fallbackIndex] = {
                ...state.messages.chats[fallbackIndex],
                status: "sent",
              };
            }
            return;
          }

          // Convert timestamps to local time for the API response
          const localizedVideoMessage = convertMessagesTimestamps([newVideoMessage])[0];

          // Find the message by tempId
          const messageIndex = state.messages.chats.findIndex(
            (chat) => chat.tempId === tempId || chat.id === tempId
          );

          if (messageIndex !== -1) {
            const existing = state.messages.chats[messageIndex];
            state.messages.chats[messageIndex] = {
              ...existing,
              ...localizedVideoMessage,
              // Always keep the pending message's own timestamp/createdAt (click-time order) —
              // never adopt the server's, which is assigned when the upload finishes and can
              // desync from true send order across a fast burst of sends.
              timestamp: existing.timestamp,
              createdAt: existing.createdAt,
              tempId: tempId,
              status: "sent",
            };
          } else {
            // If not found, add as new
            state.messages.chats = [...state.messages.chats, {
              ...localizedVideoMessage,
              tempId: tempId,
              status: "sent",
            }];

            // Sort chats by creating new array with secondary sort key
            state.messages.chats = state.messages.chats.slice().sort((a, b) => {
              const timeA = new Date(a.timestamp).getTime();
              const timeB = new Date(b.timestamp).getTime();
              
              // Primary sort: by timestamp
              if (timeA !== timeB) {
                return timeA - timeB; // Ascending order (oldest first)
              }
              
              // Secondary sort: by createdAt for identical timestamps
              // This ensures messages sent in quick succession maintain proper insertion order
              if (a.createdAt && b.createdAt) {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
              }
              
              // Fallback: use message ID if createdAt is not available
              return (a.id || '').localeCompare(b.id || '');
            });
          }
          invalidate(STORES.MESSAGES, String(sentContactId)); // fire-and-forget
          messageCache.delete(String(sentContactId));
        }
      })
      .addCase(sendVideoByUpload.rejected, (state, action) => {
        state.messageSendingLoading = false;
        // Safely extract error message
        let errorMessage = "Failed to send the video message";
        if (action.payload) {
          if (typeof action.payload === 'string') {
            errorMessage = action.payload;
          } else if (typeof action.payload === 'object' && action.payload.message) {
            errorMessage = action.payload.message;
          }
        }
        if (!takeQrRefusal(action.meta.arg?.tempId)) state.error = errorMessage;

        const { tempId } = action.meta.arg;

        // Find and update the message status
        const messageIndex = state.messages.chats.findIndex(
          (chat) => chat.tempId === tempId || chat.id === tempId
        );

        if (messageIndex !== -1) {
          state.messages.chats[messageIndex].status = "error";
        }
      })
      // Send audio by Upload
      .addCase(sendAudioByUpload.pending, (state, action) => {
        state.messageSendingLoading = true;
        state.error = null;

        // Don't add pending message here since it's already added in TextBox component
        // This prevents duplicate pending messages
      })
      .addCase(sendAudioByUpload.fulfilled, (state, action) => {
        state.messageSendingLoading = false;

        if (action.payload.success) {
          const sentContactId = action.meta.arg?.contactId;
          if (sentContactId && state.messages?.contactID &&
              String(state.messages.contactID) !== String(sentContactId)) {
            return;
          }
          if (!state.messages?.chats) return;

          const newAudioMessage = action.payload.data?.chats?.[0];
          const tempId =
            action.payload.data?.tempId ?? action.meta?.arg?.tempId;
          // WhatsApp returns chats[0]; Instagram/Facebook return messageId only — socket updates the row
          if (!newAudioMessage || !tempId) {
            return;
          }

          // Convert timestamps to local time for the API response
          const localizedAudioMessage = convertMessagesTimestamps([newAudioMessage])[0];

          // Find the message by tempId
          const messageIndex = state.messages.chats.findIndex(
            (chat) => chat.tempId === tempId || chat.id === tempId
          );

          if (messageIndex !== -1) {
            // Update with new data while preserving tempId
            state.messages.chats[messageIndex] = {
              ...localizedAudioMessage,
              tempId: tempId,
              status: "sent", // Ensure status is set to sent
            };
          } else {
            // If not found, add as new
            state.messages.chats = [...state.messages.chats, {
              ...localizedAudioMessage,
              tempId: tempId,
              status: "sent",
            }];

            // Sort chats by creating new array with secondary sort key
            state.messages.chats = state.messages.chats.slice().sort((a, b) => {
              const timeA = new Date(a.timestamp).getTime();
              const timeB = new Date(b.timestamp).getTime();
              
              // Primary sort: by timestamp
              if (timeA !== timeB) {
                return timeA - timeB; // Ascending order (oldest first)
              }
              
              // Secondary sort: by createdAt for identical timestamps
              // This ensures messages sent in quick succession maintain proper insertion order
              if (a.createdAt && b.createdAt) {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
              }
              
              // Fallback: use message ID if createdAt is not available
              return (a.id || '').localeCompare(b.id || '');
            });
          }
          invalidate(STORES.MESSAGES, String(sentContactId)); // fire-and-forget
          messageCache.delete(String(sentContactId));
        }
      })
      .addCase(sendAudioByUpload.rejected, (state, action) => {
        state.messageSendingLoading = false;
        
        // Extract error message safely (handle both string and object payloads)
        let errorMessage = "Failed to send the audio message";
        if (action.payload) {
          if (typeof action.payload === 'string') {
            errorMessage = action.payload;
          } else if (typeof action.payload === 'object') {
            errorMessage = action.payload.message || action.payload.error || JSON.stringify(action.payload);
          }
        }
        if (!takeQrRefusal(action.meta.arg?.tempId)) state.error = errorMessage;

        const { tempId } = action.meta.arg;

        // Find and update the message status
        const messageIndex = state.messages.chats.findIndex(
          (chat) => chat.tempId === tempId || chat.id === tempId
        );

        if (messageIndex !== -1) {
          state.messages.chats[messageIndex].status = "failed";
        }
      })
      // Send document by Upload
      .addCase(sendDocumentByUpload.pending, (state, action) => {
        state.messageSendingLoading = true;
        state.error = null;

        // Don't add pending message here since it's already added in TextBox component
        // This prevents duplicate pending messages
      })
      .addCase(sendDocumentByUpload.fulfilled, (state, action) => {
        state.messageSendingLoading = false;

        if (action.payload.success) {
          const sentContactId = action.meta.arg?.contactId;
          if (sentContactId && state.messages?.contactID &&
              String(state.messages.contactID) !== String(sentContactId)) {
            return;
          }
          if (!state.messages?.chats) return;

          const newDocumentMessage = action.payload.data?.chats?.[0];
          const tempId = action.payload.data?.tempId;

          // A QR send is answered "queued" with only the ids (no chat object), so there is nothing to merge here: the bubble stays as
          // it is and the live message event confirms it. Reading chats[0] unguarded used to throw inside this reducer (and undo
          // messageSendingLoading = false above) on every QR document send.
          if (!newDocumentMessage) return;

          // Convert timestamps to local time for the API response
          const localizedDocumentMessage = convertMessagesTimestamps([newDocumentMessage])[0];

          // Find the message by tempId
          const messageIndex = state.messages.chats.findIndex(
            (chat) => chat.tempId === tempId || chat.id === tempId
          );

          if (messageIndex !== -1) {
            // Update with new data while preserving tempId
            state.messages.chats[messageIndex] = {
              ...localizedDocumentMessage,
              tempId: tempId,
              status: "sent", // Ensure status is set to sent
            };
          } else {
            // If not found, add as new
            state.messages.chats = [...state.messages.chats, {
              ...localizedDocumentMessage,
              tempId: tempId,
              status: "sent",
            }];

            // Sort chats by creating new array with secondary sort key
            state.messages.chats = state.messages.chats.slice().sort((a, b) => {
              const timeA = new Date(a.timestamp).getTime();
              const timeB = new Date(b.timestamp).getTime();
              
              // Primary sort: by timestamp
              if (timeA !== timeB) {
                return timeA - timeB; // Ascending order (oldest first)
              }
              
              // Secondary sort: by createdAt for identical timestamps
              // This ensures messages sent in quick succession maintain proper insertion order
              if (a.createdAt && b.createdAt) {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
              }
              
              // Fallback: use message ID if createdAt is not available
              return (a.id || '').localeCompare(b.id || '');
            });
          }
          invalidate(STORES.MESSAGES, String(sentContactId)); // fire-and-forget
          messageCache.delete(String(sentContactId));
        }
      })
      .addCase(sendDocumentByUpload.rejected, (state, action) => {
        state.messageSendingLoading = false;
        // Safely extract error message
        let errorMessage = "Failed to send the Document message";
        if (action.payload) {
          if (typeof action.payload === 'string') {
            errorMessage = action.payload;
          } else if (typeof action.payload === 'object' && action.payload.message) {
            errorMessage = action.payload.message;
          }
        }
        if (!takeQrRefusal(action.meta.arg?.tempId)) state.error = errorMessage;

        const { tempId } = action.meta.arg;

        // Find and update the message status
        const messageIndex = state.messages.chats.findIndex(
          (chat) => chat.tempId === tempId || chat.id === tempId
        );

        if (messageIndex !== -1) {
          state.messages.chats[messageIndex].status = "error";
        }
      })
      // Mark Messages as Read
      .addCase(markMessageAsRead.pending, (state) => {
        // We don't need to set any loading state as this operation should be transparent to the user
      })
      .addCase(markMessageAsRead.fulfilled, (state, action) => {
        if (action.payload && action.payload.success && state.messages) {
          const data = action.payload.data || {};
          const hasDidUpdateFlag = Object.prototype.hasOwnProperty.call(data, "didUpdateMessages");
          const didUpdate = data.didUpdateMessages === true;
          const hasIdList = Array.isArray(data.updatedMessageIds);
          const updatedIds = hasIdList
            ? new Set(data.updatedMessageIds.map((id) => String(id)))
            : null;
          // Server explicitly reported no DB updates — do not flush the whole thread (fixes false "read").
          if (hasDidUpdateFlag && didUpdate === false) {
            return;
          }
          const useLegacyBulkMark =
            !hasDidUpdateFlag &&
            !hasIdList;

          // markMessageAsRead is ONLY for incoming messages - outgoing messages should NOT be affected.
          // Use phoneNumberID only for WhatsApp; IG/FB should not depend on it.
          const messagePlatform = state.messages.platform || "whatsapp";
          const businessSenderId =
            (messagePlatform === "whatsapp" || messagePlatform === "qr")
              ? (state.messages.wabaData?.phoneNumberID || null)
              : null;
          
          // Update the status of messages while preserving all original data
          // CRITICAL: Only update INCOMING messages (those without senderid and not from phoneNumberID)
          // Outgoing messages should only be marked as read via WhatsApp webhook, not via markMessageAsRead
          const updatedChats = state.messages.chats.map((chat) => {
            // Check if this is an outgoing message (not incoming)
            // CRITICAL: Workflow/bulk messages are also outgoing, even though they don't have senderid
            const isBulkMsg = chat.isBulkMessage === true;
            const messageId = chat.id ? String(chat.id).trim() : null;
            const tempId = chat.tempId ? String(chat.tempId).trim() : null;
            const isWorkflowMsg = (messageId && messageId.startsWith('workflow_')) || 
                                 (tempId && tempId.startsWith('workflow_'));
            const hasNoSenderId = !chat.senderid;
            const fromMatchesBusiness = businessSenderId && chat.from && 
                                       (chat.from === businessSenderId || chat.from.toString() === businessSenderId.toString());
            const isWorkflowBulkByFrom = hasNoSenderId && fromMatchesBusiness;
            const isAutomatedMessage = !chat.senderid && !chat.clientid && !chat.clientname;
            const isWorkflowBulk = isBulkMsg || isWorkflowMsg || isWorkflowBulkByFrom || isAutomatedMessage;
            
            const isOutgoingBySenderId = !!chat.senderid;
            const isOutgoingByFrom = businessSenderId && chat.from && 
                                     (chat.from === businessSenderId || chat.from.toString() === businessSenderId.toString());
            // CRITICAL: Include workflow/bulk messages as outgoing - they should NOT be marked as read by markMessageAsRead
            const isOutgoingMessage = isOutgoingBySenderId || isOutgoingByFrom || isWorkflowBulk;
            
            // DEBUG: Log for target message
            const targetMessageId = "wamid.HBgMOTIzMjY3NDQ1NTc3FQIAERgSM0MzRkY3MkZBMUYxRUFDNUFFAA==";
            const isTargetMessage = chat.id === targetMessageId || String(chat.id || '').includes("M0MzRkY3MkZBMUYxRUFDNUFF");
            if (isConsoleVerbose && isTargetMessage) {
              console.log(`🔍 [MARK AS READ] Checking target message:`, {
                messageId: chat.id,
                status: chat.status,
                senderid: chat.senderid,
                from: chat.from,
                businessSenderId,
                isWorkflowBulk,
                isOutgoingBySenderId,
                isOutgoingByFrom,
                isOutgoingMessage,
                willUpdate: !isOutgoingMessage && (chat.status === "received" || chat.status === "delivered")
              });
            }
            
            const chatIdStr = chat.id ? String(chat.id) : null;
            const shouldApplyReadFromServerList =
              updatedIds &&
              updatedIds.size > 0 &&
              chatIdStr &&
              updatedIds.has(chatIdStr);
            const shouldBulkMarkIncoming =
              useLegacyBulkMark ||
              (didUpdate && hasIdList && updatedIds.size === 0) ||
              (didUpdate && !hasIdList);

            // Only update incoming messages (those that are NOT outgoing)
            // CRITICAL: Do NOT update workflow/bulk messages - they are outgoing and should only be marked as read via webhook
            if (
              !isOutgoingMessage &&
              (chat.status === "received" || chat.status === "delivered") &&
              (shouldApplyReadFromServerList || shouldBulkMarkIncoming)
            ) {
              if (isTargetMessage) {
                console.log(`❌ [MARK AS READ] INCORRECTLY updating outgoing message to "read"!`, {
                  messageId: chat.id,
                  oldStatus: chat.status,
                  newStatus: "read"
                });
              }
              return {
                ...chat,
                status: "read",
              };
            }
            // CRITICAL: Do NOT update outgoing messages (including workflow/bulk) - they should only be marked as read via webhook
            if (isTargetMessage && isOutgoingMessage) {
              console.log(`✅ [MARK AS READ] Correctly skipping outgoing message:`, {
                messageId: chat.id,
                status: chat.status,
                reason: isWorkflowBulk ? "workflow/bulk message" : isOutgoingBySenderId ? "has senderid" : "from matches business"
              });
            }
            return chat;
          });
          
          state.messages.chats = updatedChats;

          if (state.messages.unreadCount) {
            state.messages.unreadCount = 0;
          }

          if (state.messages.markStatus) {
            state.messages.markStatus = "read";
          }
        }
      })
      .addCase(markMessageAsRead.rejected, (state, action) => {
        // We don't need to set any error state as this operation should be transparent to the user
        console.error("❌ Failed to mark messages as read:", action.payload);
      });
  },
});

// Export actions
export const {
  clearMessages,
  clearErrors,
  setInboxMessageUserError,
  addMessage,
  patchChatMessage,
  removeOptimisticMessage,
  updateMessageStatus,
  updateMessageReaction,
  updateMessageUrl,
  setExpectedContact,
  updateContactName,
} = inboxMessageSlice.actions;

// Invalidates both cache tiers for a contact's messages.
export const invalidateMessagesCache = (contactId) => {
  if (!contactId) return;
  const key = String(contactId);
  messageCache.delete(key);
  invalidate(STORES.MESSAGES, key); // fire-and-forget IDB delete
};

// Writes the given messages object into both cache tiers for a contact.
// Call after a socket message is added to Redux so IDB is always up-to-date
// (prevents refresh from serving a snapshot that's missing the new message).
// QR chats only: the snapshot must not keep this device's own UNCONFIRMED outgoing bubbles (a temp- id that is still
// pending / queued / sending, or that failed or errored before the server saved it). A QR send the server refuses is never
// stored, so re-opening the chat from this snapshot used to resurrect it as a stale "pending" clock and, sorted by its
// click-time stamp among the saved messages (which carry server times), in the wrong place. The snapshot is only a
// first-paint copy and the chat is refreshed from the database right after, so nothing real is lost. Meta, Instagram and
// Facebook chats keep the snapshot exactly as it was.
// A send this device made shows up as a bubble with a client-made `temp-` id. When the server confirms it (sent / delivered /
// read) the bubble keeps that id (only Instagram ids are swapped in place), and the thread rows the server returns never carry a
// tempId, so after the chat is closed and reopened the saved copy of the bubble and the freshly fetched row for the SAME message
// looked like two different messages (an image showed twice). This finds the confirmed temp bubbles the fetched list already
// contains, matching one to one on kind + closeness in time (+ the text, or the file size when both are known). Anything not
// confirmed yet (pending / queued / sending / failed) and anything without a temp id is never matched.
const CONFIRMED_OUTGOING_STATUSES = new Set(["sent", "delivered", "read", "seen"]);
const OPTIMISTIC_MATCH_WINDOW_MS = 2 * 60 * 1000;
export const findSupersededOptimisticChatIds = (existingChats, apiChats) => {
  const superseded = new Set();
  if (!Array.isArray(existingChats) || !Array.isArray(apiChats) || existingChats.length === 0 || apiChats.length === 0) return superseded;
  const keyOf = (c) => String(c?.id || c?._id || c?.tempId || "");
  const timeOf = (c) => {
    const t = new Date(c?.timestamp || c?.createdAt || c?.time || 0).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const kindOf = (c) => String(c?.messageType || c?.type || "");
  const apiKeys = new Set(apiChats.map(keyOf));
  const candidates = existingChats
    .filter((c) => keyOf(c).startsWith("temp-") && CONFIRMED_OUTGOING_STATUSES.has(c?.status) && !apiKeys.has(keyOf(c)))
    .sort((a, b) => timeOf(a) - timeOf(b));
  if (candidates.length === 0) return superseded;
  // A fetched row is a possible counterpart unless the client already holds it as one of ITS OWN confirmed sends (such an entry keeps the
  // tempId it was sent under, so it already explains itself). A row the client holds without a tempId came from the server -- e.g. a
  // snapshot that was saved with the temp bubble AND its real row side by side -- and is still the counterpart of that temp bubble.
  const ownByKey = new Map(existingChats.filter((c) => c?.tempId).map((c) => [keyOf(c), c]));
  const unmatchedApi = apiChats.filter((c) => !ownByKey.has(keyOf(c)) && !keyOf(c).startsWith("temp-") && c?.type !== "assignment");
  for (const cand of candidates) {
    const kind = kindOf(cand);
    let bestIdx = -1;
    let bestGap = Infinity;
    unmatchedApi.forEach((row, i) => {
      if (kindOf(row) !== kind) return;
      const gap = Math.abs(timeOf(row) - timeOf(cand));
      if (gap > OPTIMISTIC_MATCH_WINDOW_MS || gap >= bestGap) return;
      if (kind === "text" && String(row.body ?? row.message ?? "").trim() !== String(cand.body ?? cand.message ?? "").trim()) return;
      if (kind !== "text" && cand.mediaFileSizeBytes != null && row.mediaFileSizeBytes != null && Number(cand.mediaFileSizeBytes) !== Number(row.mediaFileSizeBytes)) return;
      bestIdx = i;
      bestGap = gap;
    });
    if (bestIdx !== -1) {
      superseded.add(keyOf(cand));
      unmatchedApi.splice(bestIdx, 1);
    }
  }
  return superseded;
};

const UNCONFIRMED_STATUSES = new Set(["pending", "queued", "sending", "failed", "error"]);
export const withoutUnconfirmedQrBubbles = (messages) => {
  if (messages?.platform !== "qr" || !Array.isArray(messages.chats)) return messages;
  const isUnconfirmed = (chat) => {
    const id = String(chat?.tempId || chat?.id || "");
    return id.startsWith("temp-") && UNCONFIRMED_STATUSES.has(chat?.status) && !chat?.clientid;
  };
  if (!messages.chats.some(isUnconfirmed)) return messages;
  return { ...messages, chats: messages.chats.filter((chat) => !isUnconfirmed(chat)) };
};

export const setCachedCurrentMessages = (contactId, messages) => {
  if (!contactId || !messages) return;
  const key = String(contactId);
  const result = { data: withoutUnconfirmedQrBubbles(messages), contactId: key, isInitialLoad: true, isPagination: false };
  messageCache.set(key, { data: result, timestamp: Date.now() });
  setCached(STORES.MESSAGES, key, result, TTL.MESSAGES); // fire-and-forget IDB write
};

// Export the reducer
export default inboxMessageSlice.reducer;